/**
 * `home` (§7.3) — the flat, user-ordered list of the viewer's gates. Ported from
 * `prototype/screens/home.js` + `shared.js`'s `gateCard()`. "Home is an actuator" (`flows.md`):
 * the gate-type icon is the open command, and the gear icon is the only way into Gate Detail.
 * The standard grid ("layout-grid") is the one layout where the whole tile still opens — list and
 * the spacious grid ("layout-template") swap the two icons' positions **and** confine both actions
 * to their icon (see `GateCard`'s `swapped`/`wholeTileOpens`), leaving the rest of the tile inert.
 * The standard grid trades its two icons' positions too (`global.css`), but not their actions —
 * the glyph stays decorative and the gear stays the only button, so the whole tile keeps opening.
 * **D204: inert means still.** The two layouts' press treatments moved off `.card:active` and
 * onto the controls themselves (`global.css`), so a tap on the name, the meta or the background
 * changes nothing at all — including the tap D197 (c) most has to be read as *not the control*,
 * the Bluetooth invitation that renders into `.gname-text`. The pointer cursor is scoped the same
 * way, so a desktop pointer stops claiming those surfaces are clickable either.
 *
 * Reorder keeps D113's long-press entry and sequential writes, with pointer hit-testing for
 * touch devices and a pick-up/arrow-move path for keyboard users.
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type Ref } from 'react'
import { asIsoInstant, type DenyReason, type GateId } from '@selogate/core'
import type { CachedGate } from '../../device-cache/snapshot'
import { useDeviceCache } from '../../device-cache/context'
import { openDeviceStore } from '../../device-cache'
import { RESUME_MIN_INTERVAL_MS } from '../../device-cache/resume'
import { STAGE } from '../../auth/config'
// **Not `web/src/config/`, and that is R9 §5's rule, not an oversight.** This screen is one of
// the three fail-open roots `infra/tests/client-config-client.test.ts` walks; it may register the
// one DOM node D331's notice paints into, and it may never read the document, a locale, or a
// dismissal — see the module's own docblock.
import { registerHomeNoticeHost } from '../../components/homeNoticeHost'
import {
  bleFailureKey,
  REFUSAL_KEY,
  bleAdapter,
  evaluateFreshness,
  hasCredential,
  holdsCredential,
  openLocally,
  attemptsCloudWhileLatched,
  coldFallbackLicence,
  decideFallback,
  latchedCloudPlan,
  pickTransport,
  warmCredential,
  type ColdFallbackLicence,
  type CloudVerdict,
  type RenderedLocalFailure,
  type TransportPlan,
} from '../../ble'
import { beginOpenTiming, type OpenTiming, type OpenTimingVerdict } from '../shared/openTiming'
import { useSession, type SessionApi } from '../../auth/context'
import { NetworkError, RequestTimeoutError } from '../../auth/transport'
import {
  AccessBlockLabel,
  AppBar,
  Chip,
  FrameOverlay,
  Icon,
  type IconName,
  personalizationColor,
  personalizationWash,
  SecondaryButton,
  UpdateReadyBanner,
  isViewColor,
  renderNameForViewIcon,
} from '../../components'
import type { Key } from '../../i18n/catalogs'
import { calendarDaysAgo, formatRelativeDay, formatTime } from '../../i18n/format'
import { useLocale, useTranslation } from '../../i18n/I18nProvider'
import type { Locale } from '../../i18n/locales'
import { useAppUpdate } from '../../pwa/useAppUpdate'
import { useNavigation } from '../../router'
import { accessForCard, iconForGateType, renewalDaysRemaining, SUBSCRIPTION_LOW_DAYS } from '../shared/access'
import { closeGate, closeModeOf, getLastUsedAt, getLastUsedAtAll, openGate, patchView } from '../shared/api'
import {
  nextHomeLayout,
  readHomeLayout,
  writeHomeLayout,
  type HomeLayoutPreference,
} from './layoutPreference'
import { prefersReducedMotion, type GateOpenOutcome } from './GateOpenOverlay'
import { OpenAnimation } from './OpenAnimation'
import { readOpenAnimation, type OpenAnimationStyle } from '../../theme/openAnimation'
import { matchesGateSearch } from './search'
import {
  closeFailureKey,
  contendedFailurePatch,
  closeFailurePatch,
  openFailureKey,
  isContendedOpenRefusal,
  deliveryFailurePatch,
  isNoDataFailure,
  type RenderedCloseFailure,
  type RenderedDeliveryFailure,
} from '../shared/openFailure'

interface OpenState {
  readonly phase: 'idle' | 'beaming' | 'opened' | 'denied'
  readonly pedestrian: boolean
  readonly reason?: DenyReason
  readonly genericFailure?: boolean
  /**
   * **D272 F1 — the request never left the phone.** The one cause on this path a resident can fix
   * where they are standing, and until now the only one told nothing about itself. It rides beside
   * `genericFailure` rather than replacing it, because everything else that reaches that flag is
   * still a failure with no name.
   */
  readonly noData?: boolean
  /** R1-7(d) — an access refusal `AccessBlockLabel` cannot render (its `reason` prop is core's
   * frozen `DenyReason`, not the local `OpenRefusal` `openGate()` widens to), so it gets its own
   * line beside `genericFailure`. **It is not a delivery outcome**, which is why D223's binary
   * leaves it alone: a resident refused by the grant needs the reason in order to act on it. */
  readonly notAuthorized?: boolean
  /** PEC-8: the transport could not confirm. **Not proof the gate did nothing.** */
  readonly unconfirmed?: boolean
  /**
   * **D294 — the open was refused because a CLOSE holds the gate, which is the one refusal on this
   * path that is not about a delivery.**
   *
   * It sits beside {@link deliveryFailure} rather than inside it because that field narrows a
   * `DeliveryOutcome` and a close holding the lease is not one. It selects the sentence through
   * `openFailureKey` and never the layout: `unconfirmed` is still what `metaLines` counts (D192).
   */
  readonly openRefusal?: 'gate-closing'
  /**
   * **D272/D274 — which of R6 §2's causes the transport reported, when it reported one.**
   *
   * `undefined` beside `unconfirmed: true` is the honest *did not say*: a pre-D274 server, or a
   * replay off a record written before the field. It selects the sentence and never the layout —
   * `unconfirmed` is still what `metaLines` counts, so a named cause cannot silently change the
   * tile's line budget (D192).
   */
  readonly deliveryFailure?: RenderedDeliveryFailure
  /** **The transport that actually carried it** (§6) — the result chip's job, not the plan's. */
  readonly usedTransport?: 'cloud' | 'ble'
  /** A local BLE failure, each its own sentence. See `ble/localOpen.ts`. */
  readonly bleFailure?: RenderedLocalFailure
  /**
   * **D318 — the cloud verdict this BLE leg was dispatched FOR, when one was.**
   *
   * It is carried to the render rather than consumed at dispatch because the out-of-range sentence
   * is about the whole tap and not about the radio: after an `ambiguous` verdict the cloud command
   * may already have reached the gate, and *"couldn't reach the gate over Bluetooth"* reports a
   * clean miss over it. Absent is *no cloud leg ran* — a BLE-primary plan or an offline latch —
   * which keeps `ble.localUnavailable` verbatim. `bleFailureKey` is the whole of the branch; only
   * `unavailable` reads this field.
   */
  readonly cloudVerdict?: CloudVerdict
  /**
   * **Offline, with no local path at all.** D189(e) — this is what replaced the silent no-op:
   * a tap that cannot open the gate now says which of the three reasons it is, rather than
   * doing nothing and leaving the resident to decide whether the tile is broken.
   */
  readonly bleRefusal?: 'offline-stale' | 'offline-never-synced' | 'offline-no-ble'
  /** D189(b)'s offer — see Gate Detail's `fallbackOffer`, which carries the same two fields. */
  readonly fallbackOffer?: { readonly pedestrian: boolean; readonly verdict: CloudVerdict }
  /**
   * **R7/D276 — which direction the command in flight (or just answered) carried.**
   *
   * Absent is an open, so every state this screen wrote before the Close existed reads exactly as
   * it did. It is not decoration: the tile disables its Open plate only while a **close** is in
   * flight (S6's *a Close in flight disables Open on that tile and vice versa*), so an auto-close
   * gate — which can never carry `'close'` — keeps a byte-identical Open control, which is what
   * D280's *differs from today's in exactly one respect* asserts one file over.
   */
  readonly direction?: CommandDirection
  /**
   * **A Failed close, which draws from a different vocabulary than a failed open** — R7/D277 (b)
   * inverts two of D223's rows, so `openFailure.ts` carries two maps rather than one with a
   * direction argument. This flag is what the tile renders the resolved sentence on; the two
   * fields below are what select it.
   */
  readonly closeFailed?: boolean
  /** The wire's `deliveryOutcome`, narrowed to the four a close may draw a sentence from. */
  readonly closeFailure?: RenderedCloseFailure
  /** The two refusals a close inverts or owns outright (D277 (b)/(f)). */
  readonly closeRefusal?: 'gate-busy' | 'close-not-supported'
}

/**
 * **R7/D276 — which command a tap sent.** Named rather than a bare boolean because it reaches
 * `OpenState`, where `closing: true` would read as a claim about the leaf rather than about the
 * command (D134: a receipt is not a movement).
 */
type CommandDirection = 'open' | 'close'

/**
 * 🔸 **How many gates Home warms a BLE credential for.** D189(e).
 *
 * Home cannot fall back to Bluetooth for a gate whose credential is cold, because the round trip a
 * cold one needs is the one that has just failed. So something has to warm them ahead of the tap —
 * and warming *every* gate would pull every password the resident holds into memory to serve one,
 * which is a real widening of D85's exposure for a benefit that lands on one tile.
 *
 * Three covers the realistic case: residents open the gate they always open. The bound is the
 * decision; the ordering below (last-used first, then the resident's own sort) is how it is spent.
 */
const HOME_WARM_LIMIT = 3

/**
 * 🔸 **The floor between two warm attempts for the same gate.**
 *
 * Gate Detail does not need one: its effect keys off a single gate and a boolean that only goes
 * false→true on success. Home's target *list* rotates — `lastUsedAt` moves after every open — so a
 * failed warm can be re-armed by an unrelated tile changing places. This is the brake for that,
 * and it is the second of two: the effect key below is the first.
 */
const WARM_RETRY_FLOOR_MS = 60_000

/**
 * **The floor between two last-used reads of the same gate** — the reopen burst (`STATE.md` §5,
 * 2026-09-25), and the stamp effect's docblock says why a read in flight counts.
 *
 * Half of {@link RESUME_MIN_INTERVAL_MS} rather than equal to it, and the ratio is the rule: the
 * two list deliveries of one cold reopen land about a second apart and must fall inside it, while
 * two resume refreshes land at least a resume floor apart and must each read once. Derived rather
 * than restated so the two cannot drift across one another.
 */
const LAST_USED_READ_FLOOR_MS = RESUME_MIN_INTERVAL_MS / 2

/**
 * 🔸 How long a fallback offer stays on a tile before the tile resets.
 *
 * The ordinary result reset is 1.6 s, which is right for a chip and far too short for a control
 * the resident has to notice, read a warning above, and press. Matched to Gate Detail's
 * `COMMAND_RESULT_MS` so the same offer does not have two lifetimes.
 */
const FALLBACK_OFFER_MS = 10_000

const IDLE: OpenState = { phase: 'idle', pedestrian: false }

/**
 * D104/D121/D163 — bare `"Opened 3:14 PM"` **today**, and the day word alone on any earlier
 * calendar day (D246): `"Opened yesterday"` / `"נפתח שלשום"`. With no actor either way: the
 * viewer's own most recent open, never another member's.
 *
 * A clock time with no day is the defect this replaced — a gate opened three weeks ago read
 * "שימוש אחרון 14:30", which is a true time attached to a day the tile never named, and reads as
 * *today* on the one screen where "when did I last open this" is the whole question. The split is
 * on the **calendar** day (`calendarDaysAgo`), not on elapsed hours, so an open at 23:50 stops
 * being "today" at midnight rather than twenty-four hours later.
 *
 * **The label is a verb because the day word would not fit.** `שימוש אחרון` plus `לפני N ימים`
 * wrapped the list pill to two lines for **every** count from three days up — measured in Chrome
 * at 375px, where this line has 158px — which is the common case rather than a rare one, and a
 * taller pill also opens D180's bite wider around the puck. Only `היום`-equivalent clock times and
 * the two single-word days (`אתמול`, `שלשום`) ever fit under the old label. Shortening the label
 * bought the width back in all five locales; the counted form and the layouts are untouched. The
 * other locales take the same verb rather than a noun phrase, and Russian and Ukrainian take the
 * **impersonal** form (`Открыто` / `Відкрито`) so the line carries no person, gender or number —
 * D200's rule that an action label in a log agrees with nothing, reached here through width.
 *
 * 🔸 The four non-Hebrew strings are my translations, and **two of them still wrap and are shipped
 * that way**: measured in the same 158px box, Russian goes to two lines from **5 days** and
 * Ukrainian from **14** (`Відчинено` wrapped from 2, which is why it is `Відкрито`), because
 * `дней назад` / `днів тому` are simply longer than `לפני N ימים`. Hebrew — the default locale
 * (D124) and the pilot's — English and Italian fit at every count out to a year. Closing the last
 * two needs an **abbreviation** (`Посл.` / `Відкр.` both fit outright), which is a copy-quality
 * trade rather than a layout one, so it is left to the owner rather than taken here.
 *
 * `undefined` = not fetched, or the viewer has never opened this gate themselves, and renders
 * nothing; a first launch is therefore silent rather than a placeholder.
 */
function lastUsedTextFor(
  gateId: string,
  lastUsedAt: Readonly<Record<string, string | null>>,
  locale: Locale,
  t: (key: Key<'home'>, vars?: Readonly<Record<string, string | number>>) => string,
): string | undefined {
  const iso = lastUsedAt[gateId]
  if (iso === undefined || iso === null) return undefined
  try {
    const instant = asIsoInstant(iso)
    // The catalogs' variable stays `{{time}}` although a day word can now fill it. Renaming it is
    // five JSON files with no CI gate behind it — `catalogs.test.ts` asserts key parity, not
    // variable names — so a missed file renders a literal `{{time}}` on a tile.
    const time =
      calendarDaysAgo(instant) === 0 ? formatTime(instant, locale) : formatRelativeDay(instant, locale)
    return t('lastUsed', { time })
  } catch {
    return undefined
  }
}

/**
 * The one BLE sentence a tile has to say, or nothing.
 *
 * A refusal (*nothing can open this gate right now*) and a local failure (*the exchange did not
 * work*) are mutually exclusive on one tile — the first means no command was attempted — so they
 * collapse to one line rather than two that can never both appear.
 */
function bleLineTextFor(
  openState: OpenState,
  tBle: (key: Key<'ble'>) => string,
): { readonly bleLineText?: string } {
  if (openState.bleRefusal !== undefined) return { bleLineText: tBle(REFUSAL_KEY[openState.bleRefusal]) }
  if (openState.bleFailure !== undefined)
    return { bleLineText: tBle(bleFailureKey(openState.bleFailure, openState.cloudVerdict)) }
  return {}
}

/**
 * Set by the first `GET /activity?last=true` answered in the pre-D337 shape, and never unset: the
 * stage a bundle talks to does not change under it. Module state, so a remount does not re-ask.
 */
let stageLacksBatchLastUsed = false

/** Test seam. */
export function __resetLastUsedStageForTests(): void {
  stageLacksBatchLastUsed = false
}

/**
 * **D337 — Home's stamps, one request per round**, and the per-gate read only where the stage
 * predates it.
 *
 * `getLastUsedAtAll` answers `data: undefined` on exactly one condition — a `2xx` without D337's
 * shape, which is an older stage reading `?last=true` as nothing — and that is the **only** thing
 * that falls back. **A refusal does not**: a `429` or a `503` is the throttled reopen D337 exists to
 * relieve, and answering it with eight more requests is the burst rebuilt inside its own fix. A
 * failed round renders no stamp, which is the line's *never opened* reading — silence, never a
 * wrong day. `undefined` in an entry means *no answer for this gate*, and the merge below skips it.
 *
 * **The fallback is learned once per process, and then the batch is not asked again.** A stage
 * that predates D337 answers the batch URL with the cross-gate history, which for a manager is the
 * most expensive read in the product (D157), so asking it every round would add that read plus an
 * execution to every reopen, which is worse than the pre-D337 client. The stage is fixed for the
 * life of the bundle, so one old-shape answer settles it. And **the fallback reads only the gates
 * that are due**, `dueGateIds`, never the whole list: one gate arriving (an invite accepted) is one
 * per-gate read, as it was before D337, not N.
 */
async function readLastUsedStamps(
  request: SessionApi['request'],
  dueGateIds: readonly string[],
): Promise<ReadonlyArray<readonly [string, string | null | undefined]>> {
  if (!stageLacksBatchLastUsed) {
    try {
      const batch = await getLastUsedAtAll(request)
      if (!batch.ok) return []
      if (batch.data !== undefined) return [...batch.data]
      stageLacksBatchLastUsed = true
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      return []
    }
  }
  return Promise.all(
    dueGateIds.map(async (gateId) => {
      try {
        const result = await getLastUsedAt(request, gateId as GateId)
        return [gateId, result.ok ? result.data : undefined] as const
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error
        return [gateId, undefined] as const
      }
    }),
  )
}

function orderGates(gates: readonly CachedGate[]): readonly CachedGate[] {
  return [...gates].sort((a, b) => {
    const orderA = Number.isFinite(a.sortOrder) ? (a.sortOrder as number) : gates.indexOf(a)
    const orderB = Number.isFinite(b.sortOrder) ? (b.sortOrder as number) : gates.indexOf(b)
    return orderA - orderB
  })
}

export function HomeScreen({
  onMenu,
  menuButtonRef,
}: {
  readonly onMenu: () => void
  readonly menuButtonRef?: Ref<HTMLButtonElement>
}): JSX.Element {
  const deviceCache = useDeviceCache()
  const session = useSession()
  const navigate = useNavigation()
  const { t } = useTranslation('home')
  const { t: tCommon } = useTranslation('common')
  /**
   * D275 — Home is where the downloaded-update banner lives, and this is its only consumer.
   *
   * **The screen reads the rail rather than being handed it**, exactly as `AppBar` does one
   * element up: `useAppUpdate` is a `useSyncExternalStore` selection over two module stores, so a
   * third subscriber costs a listener and threading it down from `App` would put a shell concern
   * into this screen's props. **The component is P6's** (`components/UpdateReadyBanner.tsx`) and
   * only its placement is P7's, which is the shape §4.8 already gives the sidebar's rows.
   */
  const { installReady: updateInstallReady, apply: applyUpdate } = useAppUpdate()
  /** P16's namespace — the local-failure copy, the offline refusals, and D189's fallback control. */
  const { t: tBle } = useTranslation('ble')
  const { dir, locale } = useLocale()

  const [order, setOrder] = useState<readonly string[]>(() =>
    orderGates(deviceCache.gates).map((g) => g.gateId),
  )
  const [sorting, setSorting] = useState(false)
  const [pickedUp, setPickedUp] = useState<string | undefined>(undefined)
  const [reorderFailed, setReorderFailed] = useState(false)
  /**
   * D32/D35 — client-side only, matched against `deviceCache.gates` (already the viewer's own
   * scope) rather than any second source, so a gate the viewer holds no membership on is absent
   * because it was never in the list. Cleared on entering reorder mode: dragging a filtered
   * subset would move tiles relative to positions the user cannot see, so sort mode always acts
   * on the full order.
   */
  const [query, setQuery] = useState('')
  /**
   * Layout and density are **device-local and read synchronously** (`layoutPreference.ts` says
   * why neither the server nor the IndexedDB snapshot store is the right home). The lazy
   * initializer matters: reading in an effect instead would render the default first and correct
   * it a frame later, which is a visible jump on every launch.
   */
  const [layout, setLayout] = useState(() => readHomeLayout())
  const { grid, spacious } = layout

  /**
   * One writer, so a control added later cannot persist one half of the pair and not the other.
   *
   * Two details, both easy to undo. The write is deliberately **outside** the state updater:
   * React double-invokes updaters under StrictMode, and a persistence call belongs to the event
   * rather than to the reducer. And the base is read from a **ref**, not from the `layout` the
   * render closed over — the same reason `orderRef` exists a few lines down. Two toggles inside
   * one tick both close over the pre-tick value, so `{...layout}` silently drops the first: tap
   * grid then density and you get standard density and *no grid*, with the stored row agreeing.
   */
  const layoutRef = useRef(layout)
  layoutRef.current = layout

  function updateLayout(patch: Partial<HomeLayoutPreference>): void {
    const next = { ...layoutRef.current, ...patch }
    layoutRef.current = next
    setLayout(next)
    writeHomeLayout(next)
  }
  const [openStates, setOpenStates] = useState<Readonly<Record<string, OpenState>>>({})
  /**
   * **D295 — the gates carrying a command in flight, as a SYNCHRONOUS mirror of `openStates`'
   * `beaming` phase.**
   *
   * It is a ref and not a read of `openStates` because the guard has to answer *before* React
   * re-renders: `runCommand` closes over the `openStates` of the render that created it, and the
   * `beaming` write a tap makes is a state update, so a second tap arriving in the same frame
   * would read the pre-tap value and dispatch.
   *
   * **The mirror is maintained in `setOpenState` and nowhere else** — the single place every phase
   * this screen writes passes through, which is `gateLifecycleWrite()`'s argument for the
   * last-manager guard one layer down: a command path added later inherits the release rather than
   * having to remember it. `settleFallback`'s direct `setOpenStates` call is deliberately not a
   * second writer — it spreads the previous state and changes no phase.
   */
  const inFlightRef = useRef(new Set<string>())
  /**
   * **The result-reset timer per gate, so a previous command's reset cannot disarm D295's guard
   * over the current one** (bugs-hunt row 13 F2).
   *
   * Every result this screen writes — `opened`, `denied`, a Close's outcome — is followed by a
   * timer that returns the tile to `IDLE` ~1.6 s later. Those timers used to be anonymous: no
   * handle, nothing cleared. A resident who read a result and tapped again inside that window
   * put a second command in the air, and the *first* command's timer then fired, wrote `IDLE`
   * through `setOpenState`, and — because the release is keyed on the phase — deleted the gate
   * from `inFlightRef` while the second exchange was still running. The tile snapped to idle, the
   * Close plate's `disabled` lifted with it, and a third tap dispatched the third command D295
   * exists to drop, reading its `409 gate-busy` as a green *sent* (D223).
   *
   * **The clear-point is `setOpenState`'s `beaming` branch, and nowhere else** — the same single
   * writer that maintains `inFlightRef`, so the two cannot drift: the write that marks a gate in
   * flight is the write that disarms whatever reset was still pending against it. `resetLater`
   * is the only scheduler and replaces any handle it finds, so a path that reaches two results
   * (the fallback's rethrow) keeps the later one. **Do not clear on every non-`beaming` write**:
   * a result write is followed by its own `resetLater`, and a clear there would be a no-op that
   * reads as if it decided something.
   */
  const resetTimerRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  /**
   * The gate-opening overlay, and there is **at most one** — `runCommand` is per gate and Home
   * guards nothing globally, so two tiles can be in flight at once, but two overlays would be two
   * of every `sg-` filter id in one document. A **pending** overlay wins: a second open while one
   * is still waiting runs its normal tile flow, which is what actually reports it.
   *
   * **An ANSWERED one does not win, and that asymmetry is the whole of the fix here.** The tile
   * resets to idle 1.6 s after the receipt (D240's own timer, the one that may carry no second
   * platform read) while the overlay's exit runs `MIN_WAIT_MS` + `ANSWER_HOLD_MS` + `FADE_OUT_MS`
   * past it — 640 ms on an ordinary cloud open, 1240 ms when the wait floor pads a fast one. In
   * that gap the tile says *Open* and invites a tap that the raise below then refused to draw,
   * so the resident got the whole command, `שולח…` → `נפתח` and no animation, intermittently and
   * with nothing on screen explaining it. An overlay that has already swung its leaves has said
   * everything it has to say; a new command supersedes it. A **pending** one is a wait over a
   * command still in the air and is never replaced, which is what keeps D229's handover intact.
   *
   * `seq` is that supersession made visible to React: it is the overlay's `key`, so the
   * replacement is a genuinely fresh `GateOpenOverlay` with its own `mountedAt` and its own
   * `answered`/`leaving` — without it the new open would reuse the old instance's clock and
   * re-enter mid-exit. `answerOverlay` spreads `...prev`, so `seq` survives the answer exactly
   * as `style` does.
   */
  const [overlay, setOverlay] = useState<
    | {
        readonly gateId: string
        readonly outcome: GateOpenOutcome
        readonly style: OpenAnimationStyle
        readonly seq: number
      }
    | undefined
  >(undefined)
  /** Monotonic per mount; only ever read through `raiseOverlay` below. */
  const overlaySeq = useRef(0)
  const cardRefs = useRef(new Map<string, HTMLDivElement>())
  const orderRef = useRef(order)
  const pointerDrag = useRef<{ readonly gateId: string; readonly original: readonly string[] }>()
  orderRef.current = order

  useEffect(() => {
    // A gate added/removed since the last render (a fresh install, a revoke) reconciles into the
    // existing order rather than resetting it — new gates append, gone ones drop. The append
    // side is sorted by `sortOrder` (`orderGates`, the same reader the initial `useState` above
    // uses) rather than left in the cache's raw array order: `deviceCache.gates` starts empty and
    // this effect is what actually populates `order` on every launch (the `useState` initializer
    // above only ever sees an empty list), so a raw-order append here was the one place a
    // reorder's `sortOrder` was read back and then silently ignored.
    const known = new Set(order)
    const current = new Set(deviceCache.gates.map((g) => g.gateId))
    const appeared = orderGates(deviceCache.gates.filter((g) => !known.has(g.gateId)))
    const next = [...order.filter((id) => current.has(id)), ...appeared.map((g) => g.gateId)]
    if (next.length !== order.length || next.some((id, i) => id !== order[i])) setOrder(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceCache.gates])

  const byId = useMemo(() => new Map(deviceCache.gates.map((g) => [g.gateId, g])), [deviceCache.gates])
  const orderedGates = order.map((id) => byId.get(id)).filter((g): g is CachedGate => g !== undefined)
  const visibleGates =
    query.trim() === '' ? orderedGates : orderedGates.filter((gate) => matchesGateSearch(query, gate))

  /**
   * D104/D121/D141/D163 — every tile's last-used stamp, scoped to the viewer's own opens.
   * **Never `gate.lastSeenAt`** (D141/D142's "forbidden repair": that field moves on a status
   * probe exactly as on an open, so rendering it as "last used" would claim an opening nothing
   * witnessed). Fetched for every gate at once through P9's own read (`GET /activity?last=true`,
   * **D337**) — a parameter of the manager-only Activity route, not a second reader, and the one
   * read on this screen not behind D29; the per-gate `GET /gates/{id}/activity?last=true` is the
   * fallback for a stage that predates it (`readLastUsedStamps`).
   *
   * **Every role fetches now.** D141 removed the LED from Home for managers as well as
   * residents and gave every card this line instead; D163 scoped the read itself to the
   * caller's own opens, so a manager and a resident of the same gate call this identically —
   * neither ever sees another member's timestamp.
   *
   * **`runCommand` also writes here directly on a successful open**, so the stamp is already
   * current by the time the "Sent" chip clears — a resident who just used the gate would
   * otherwise see their own last-used time turn stale for however long until the next fetch (a
   * remount, or the app bar's refresh control), which reads as broken rather than merely late.
   *
   * **Each gate's stamp is asked at most once per {@link LAST_USED_READ_FLOOR_MS}, and a read in
   * flight counts as asked** — the reopen burst (`STATE.md` §5, 2026-09-25). **D337 then made the
   * round ONE request**, so the floor now decides how many rounds a reopen runs rather than how
   * many requests each one costs. **It stays per gate for the fallback**: on a stage that predates
   * D337, only the due gates are read, and a single timestamp could not say which those are. A cold reopen hands
   * this effect the list **twice**, the on-device snapshot and then `GET /gates` ~1 s later, each a
   * new array of the same gates; keyed on that identity alone, the effect re-read every gate on
   * both, and production's access log shows the doubled round — 16 reads for 8 gates in one second
   * — beside an Open that Lambda's ten-execution ceiling then throttled. The floor is **per gate
   * and stamped at the request**, `warmAttemptedAt`'s shape below, so a gate that appears later
   * (an invite accepted, a fresh install) is read the moment it arrives. Three things not to
   * undo. **A superseded round's answers are applied, never dropped**: the old `cancelled` flag
   * discarded them on the next list change, which only worked because the next change asked
   * again — with the floor, nothing does. **The floor is HALF the resume floor, not equal to
   * it**, so every resume that refreshes the list (≥ `RESUME_MIN_INTERVAL_MS` apart) still reads
   * the stamps once — an open made from another device must reach this tile without a relaunch —
   * while the two deliveries of one reopen, a second apart, fall inside it. And **a failed read
   * is not retried inside the window**: a throttled reopen is exactly when re-asking costs the
   * most, and the line is a convenience that renders nothing when absent.
   */
  const [lastUsedAt, setLastUsedAt] = useState<Readonly<Record<string, string | null>>>({})
  const lastUsedReadAt = useRef(new Map<string, number>())
  /**
   * How many stamp rounds are in the air — the warm effect below waits on it. Kept twice on
   * purpose: the **ref** because the stamp effect and the warm effect run in the same commit, in
   * declaration order, so the warm effect must see this round *before* React re-renders; the
   * **state** because the round settling is what has to re-run the warm effect.
   */
  const stampRoundsRef = useRef(0)
  const [stampRounds, setStampRounds] = useState(0)

  useEffect(() => {
    const now = Date.now()
    const gateIds = deviceCache.gates.map((g) => g.gateId)
    const due = gateIds.filter((gateId) => {
      const askedAt = lastUsedReadAt.current.get(gateId)
      return askedAt === undefined || now - askedAt >= LAST_USED_READ_FLOOR_MS
    })
    if (due.length === 0) return
    // One request answers every gate (D337), so every gate on screen counts as asked — not only
    // the one whose arrival made the round due. On a stage that predates it, only the due gates
    // are read, so only they are marked: the per-gate floor is what keeps that path to one read
    // per arriving gate.
    for (const gateId of stageLacksBatchLastUsed ? due : gateIds) lastUsedReadAt.current.set(gateId, now)
    stampRoundsRef.current += 1
    setStampRounds((n) => n + 1)
    void readLastUsedStamps(session.request, due)
      .then((entries) => {
        setLastUsedAt((prev) => {
          const next = { ...prev }
          for (const [gateId, value] of entries) {
            if (value === undefined) continue
            const current = next[gateId]
            // This fetch can resolve after `runCommand`'s own optimistic stamp (below) if it was
            // already in flight when the open completed — a `null`/older answer here is then stale,
            // not fresher, and must not regress a value the open itself just proved.
            if (current !== undefined && current !== null && (value === null || value < current)) continue
            next[gateId] = value
          }
          return next
        })
      })
      .finally(() => {
        stampRoundsRef.current -= 1
        setStampRounds((n) => n - 1)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceCache.gates])

  /**
   * **Warm a bounded set of BLE credentials while the network still exists** — D189(e), and the
   * thing without which Home's fallback cannot exist.
   *
   * A cold credential needs an `ble-access` round trip, which is exactly the request that has just
   * failed when cloud could not carry a command. So a tile can only fall back to Bluetooth for a
   * gate whose credential is already in hand, and nothing on this screen would put one there.
   *
   * **Which gates, and why not all of them.** Last-used first, then the resident's own order,
   * capped at {@link HOME_WARM_LIMIT}. Warming everything would pull every password the resident
   * holds into memory to serve one tap — a real widening of D85's bound for a benefit that lands
   * on one tile — and residents overwhelmingly open the gate they always open.
   *
   * **`bleConfigured` is not available here and cannot be made available.** `CachedGate` drops it
   * (`snapshot.ts`, with a test asserting its absence) *and* the gate projection emits it to
   * managers only — so a resident, who is the entire audience for this feature, could never
   * receive it whatever the snapshot carried. The read is therefore *unknown ⇒ permitted*, the
   * same fail-open-for-display rule Gate Detail already applies. Being wrong costs at most
   * {@link HOME_WARM_LIMIT} refused round trips per launch, and `warmCredential` never throws.
   *
   * **The warms wait for the network to go quiet — the list read and every stamp round settled
   * — and that wait is where the ordering above starts being true.** They used to fire at the
   * snapshot paint, inside the reopen burst that `STATE.md` §5 (2026-09-25) records throttling a
   * resident's Open against Lambda's ten-execution ceiling, and with no stamp in hand yet — so
   * they warmed the first three tiles *by sort order*, and when the stamps landed and the targets
   * rotated to the gates actually used last, they warmed those as well: six `ble-access` calls for
   * a limit of three, the first three spent on the wrong gates. Two things not to undo. **The gate
   * reads the stamp rounds from a ref as well as from state**, because the stamp effect is
   * declared above this one and runs first in the same commit — a state read alone lets the warm
   * fire in the very commit that starts a round. And **`refreshing` is a term** because a cold
   * reopen paints from the snapshot while `GET /gates` is still out, and a resume starts its list
   * refresh before anything answers; without it the paint is the moment the warms go. 🔸 The
   * stated cost: on a **cold** launch nothing is held (`held` is in memory), so a tap in the first
   * second or two whose cloud leg then fails has no warm credential to fall back on — a window the
   * paint-time warm only raced rather than closed, its own round trip being as long.
   */
  const warmRequestRef = useRef(session.request)
  warmRequestRef.current = session.request
  const warmAttemptedAt = useRef(new Map<string, number>())

  const warmTargets = useMemo(() => {
    if (!deviceCache.online || !bleAdapter().supported()) return [] as readonly string[]
    const byLastUsed = [...deviceCache.gates]
      .filter((g) => typeof lastUsedAt[g.gateId] === 'string')
      .sort((a, b) => (lastUsedAt[b.gateId] ?? '').localeCompare(lastUsedAt[a.gateId] ?? ''))
      .map((g) => g.gateId)
    const seen = new Set<string>()
    const targets: string[] = []
    for (const gateId of [...byLastUsed, ...order]) {
      if (seen.has(gateId) || !deviceCache.gates.some((g) => g.gateId === gateId)) continue
      seen.add(gateId)
      targets.push(gateId)
      if (targets.length === HOME_WARM_LIMIT) break
    }
    return targets as readonly string[]
  }, [deviceCache.online, deviceCache.gates, lastUsedAt, order])

  /*
   * **Two independent brakes, and both are needed** — this is §4.9's 2,965-request loop arriving
   * on a second screen, where it has one more way to fire than it did on the first.
   *
   * `session.request` gets a **new identity after every API call the app makes**
   * (`SessionManager.request()` notifies on every non-401 response whether or not anything
   * changed), so it is held in a ref and kept out of the deps, exactly as on Gate Detail.
   *
   * The **string key** is the first brake: `deviceCache.gates` is a fresh array on every refresh,
   * so an array dependency would re-arm this on cache churn alone. The key changes only when the
   * target *set* or its credential state actually changes.
   *
   * The **stamp map** is the second, and it is the one Gate Detail does not need. Its effect
   * watches a single gate and a boolean that goes false→true only on success, so a failed warm
   * simply stops. Home's target list *rotates* — `lastUsedAt` moves after every open — so without
   * a floor, an unrelated tile changing places could re-arm a warm that has already failed, over
   * and over, which is the same unbounded loop wearing a different trigger.
   */
  const warmKey = warmTargets.map((gateId) => `${gateId}:${hasCredential(gateId) ? '1' : '0'}`).join(',')
  const networkBusy = deviceCache.refreshing || stampRounds > 0
  useEffect(() => {
    if (warmKey === '') return
    if (networkBusy || stampRoundsRef.current > 0) return
    const now = Date.now()
    for (const entry of warmKey.split(',')) {
      const gateId = entry.slice(0, entry.lastIndexOf(':'))
      if (entry.endsWith(':1')) continue
      if (now - (warmAttemptedAt.current.get(gateId) ?? 0) < WARM_RETRY_FLOOR_MS) continue
      warmAttemptedAt.current.set(gateId, now)
      void warmCredential(warmRequestRef.current, gateId)
    }
  }, [warmKey, networkBusy])

  function setOpenState(gateId: string, patch: OpenState): void {
    // **D295** — the mirror above, kept here so it cannot drift from the phase it mirrors. A
    // **close** marks the gate too: one gate lease answers both directions, so an open dispatched
    // behind a close in flight is the same refused second command as the reverse, which is
    // `closeButton`'s own reading of D276 applied to the two paths that carry no `disabled`.
    if (patch.phase === 'beaming') {
      inFlightRef.current.add(gateId)
      // Row 13 F2 — a reset still pending from the previous command would fire over this one.
      const pending = resetTimerRef.current.get(gateId)
      if (pending !== undefined) {
        clearTimeout(pending)
        resetTimerRef.current.delete(gateId)
      }
    } else inFlightRef.current.delete(gateId)
    setOpenStates((prev) => ({ ...prev, [gateId]: patch }))
  }

  /** Return the tile to `IDLE` after `ms`, holding the handle so a new command can disarm it. */
  function resetLater(gateId: string, ms: number): void {
    const pending = resetTimerRef.current.get(gateId)
    if (pending !== undefined) clearTimeout(pending)
    resetTimerRef.current.set(
      gateId,
      setTimeout(() => {
        resetTimerRef.current.delete(gateId)
        setOpenState(gateId, IDLE)
      }, ms),
    )
  }

  /**
   * **The one raise**, called from `runCommand` and `runClose` past each one's refusals — never
   * before them (D229: no wait is drawn over a command that did not leave the device). It is a
   * function rather than the condition written twice because the condition is the decision: two
   * copies of it is how the open half and the close half come to disagree about when a resident
   * sees an animation, which is the divergence D240 removed one timer over.
   *
   * Returns nothing and is safe to call unconditionally — reduced motion mounts nothing rather
   * than reducing (D198), and a **pending** overlay is left alone.
   */
  function raiseOverlay(gateId: string): void {
    if (prefersReducedMotion()) return
    setOverlay((prev) =>
      prev !== undefined && prev.outcome === 'pending'
        ? prev
        : // The style is read once, here, and held for the whole of this open: a resident who
          // changes the pill mid-flight must not have the choreography swapped underneath them.
          { gateId, outcome: 'pending', style: readOpenAnimation(), seq: ++overlaySeq.current },
    )
  }

  /**
   * Hands the overlay its answer, and only if the overlay on screen is the one this open put
   * there. A second gate's open finishing first must not swing the leaves for a command still in
   * flight — and once the overlay has an answer it is running its own exit, so a later write would
   * restart a choreography that has already begun.
   */
  function answerOverlay(gateId: string, outcome: GateOpenOutcome): void {
    setOverlay((prev) =>
      // `...prev` rather than `{ gateId, outcome }`: the style was chosen when the overlay was
      // raised and must survive the answer — rebuilding the object here would drop it and swing
      // the wrong animation mid-open.
      prev !== undefined && prev.gateId === gateId && prev.outcome === 'pending'
        ? { ...prev, outcome }
        : prev,
    )
  }

  /**
   * **The local path from a tile** — PEC-11's *"show local success immediately"*, over the same
   * `openLocally()` Gate Detail uses so D25's receipt rule has exactly one implementation.
   *
   * Returns the overlay's answer rather than setting it, because the caller owns the overlay's
   * lifetime and answering it from two places is how a second gate's open comes to swing the
   * leaves for a command still in flight.
   */
  async function dispatchLocal(
    gate: CachedGate,
    pedestrian: boolean,
    verdict?: CloudVerdict,
    /**
     * R6 A3 — the leg this dispatch belongs to. Absent for the offer button, which is the
     * resident's **own** second gesture and therefore its own tap, with its own `tapId`.
     */
    timing?: OpenTiming,
  ): Promise<GateOpenOutcome> {
    /*
     * **R4 §7.1b, on the tile — where it was true of the overlay and false of the sentence.**
     *
     * *"No leg's failure is rendered while another leg is still to run."* R4 recorded Home as
     * already obeying that, on the ground that *"`settleFallback`'s outcome overwrites the cloud
     * one and the overlay is answered once, after"* — both true, and both about the **overlay**.
     * The tile's own `openState` was not overwritten: every caller reaching here through
     * `settleFallback` has just written the cloud leg's refusal — PEC-8's *"sent, unconfirmed"*,
     * or the generic failure line — and this path then awaited the whole BLE exchange without
     * touching it. So the tile printed **the failed leg's sentence over a gate that was opening**,
     * through the connect, the 250 ms settle and the reply window, and only flipped to *Sent*
     * when the exchange landed.
     *
     * **On the web leaf nothing showed it**, which is why it survived D189(e): the standing offer
     * hid that sentence (`!offering`) and the resident's own tap set `beaming` before dispatching.
     * D240 removed both from the container, and the first hardware run of its observation (1)
     * reported exactly this — one tap, the gate opens, the failure sentence beside it.
     *
     * It is stated here rather than at the three call sites for the reason the whole of D240 is
     * one file: a dispatch that announced its own start in some callers and not others is the
     * divergence this seam keeps paying for. The transport is named from the start because this
     * attempt is BLE by construction — there is no fallback left to change it. Gate Detail's
     * `dispatchLocal` carries the same line, and both are pinned against a radio held open
     * mid-exchange: an adapter that resolves in the same tick cannot see this window at all.
     */
    setOpenState(gate.gateId, { phase: 'beaming', pedestrian, usedTransport: 'ble' })
    // R4/D217 — `never-dispatched` means the request never reached the network, which is fresher
    // than `deviceCache.online` (read from the render this closed over). Passing it through
    // suppresses the expiry check so a held-but-lapsed credential is used instead of re-fetched
    // over a network that is gone. Gate Detail's `dispatchLocal` carries the full argument.
    const reachedNetwork = verdict !== 'never-dispatched'
    /*
     * **R6 A3 — defaulted to the throwing exit, and delivered by the `finally` below.**
     *
     * Every path out of this function is a verdict, the thrown one included — which is D229's own
     * argument for answering the overlay from a `finally`, applied to the measurement instead of
     * to the animation. A leg that left no line is a hole in the distribution, and a hole is
     * indistinguishable from a tap that was never made.
     */
    let timingVerdict: OpenTimingVerdict = 'failed'
    let timingReason = 'threw'
    try {
      const result = await openLocally({
        request: session.request,
        gateId: gate.gateId,
        command: pedestrian ? 'pedestrian' : 'open',
        online: deviceCache.online && reachedNetwork,
        userId: session.userId,
        stage: STAGE,
        openStore: openDeviceStore,
      })
      if (result.ok) {
        setOpenState(gate.gateId, { phase: 'opened', pedestrian, usedTransport: 'ble' })
        setLastUsedAt((prev) => ({ ...prev, [gate.gateId]: new Date().toISOString() }))
        timingVerdict = 'opened'
        timingReason = 'delivered'
        /*
         * **No `GET /gates` here.** A BLE open never reaches the cloud, so nothing in that
         * response is about it — the stamp above is this screen's own, and `openLocally`'s rule 5
         * is what uploads the receipt. It was also the more expensive of the two vestigial
         * refreshes: it writes a new `gates` array identity, which is the last-used effect's
         * dependency, so one local open re-fetched `lastUsedAt` for **every** gate against
         * D68/D90's shared ceiling.
         */
        return 'opened'
      }
      /*
       * **R4/D223 — the controller answered, so this is Success** (consuming D216's verdict).
       * `CMD:Err` is the gate's ordinary reply to an open requested while the leaf is already
       * moving: it heard the command and declined it, which is exactly the evidence the cloud
       * path's `rejected` carries and which `toOpenResult()` now routes to `'opened'`. The two
       * transports have to answer the same tap the same way.
       *
       * **`lastUsedAt` is deliberately not stamped.** D163's line is *the viewer's own most recent
       * open*, and the opening this refusal is evidence of belongs to the **previous** tap, which
       * stamped for itself. Stamping again would move the line for a command that moved nothing.
       */
      if (result.reason === 'refused') {
        setOpenState(gate.gateId, { phase: 'opened', pedestrian, usedTransport: 'ble' })
        timingVerdict = 'opened'
        timingReason = 'refused'
        return 'opened'
      }
      setOpenState(gate.gateId, {
        phase: 'denied',
        pedestrian,
        usedTransport: 'ble',
        bleFailure: result.reason,
        // D318 — the leg this dispatch was made for. `exactOptionalPropertyTypes` is on, so an
        // explicit `undefined` does not assign and spreading `{}` is what says *no cloud leg ran*.
        ...(verdict === undefined ? {} : { cloudVerdict: verdict }),
      })
      /*
       * **R6 A3 — a BLE `timeout` is `unconfirmed`, not `failed`, and the two must not merge.**
       * PEC-8: a silence is not proof the gate did nothing, and R6 §2's F11 is a different row
       * from F9's out-of-range. Every other reason is a local refusal that delivered nothing.
       */
      timingVerdict = result.reason === 'timeout' ? 'unconfirmed' : 'failed'
      timingReason = result.reason
      return 'refused'
    } finally {
      timing?.settle({ transport: 'ble', verdict: timingVerdict, reason: timingReason })
    }
  }

  /**
   * **PEC-13, applied** — the rule itself is `decideFallback`, one copy shared with Gate Detail
   * (D240), because two screens answering one decision differently is the failure this seam had.
   * What stays here is the *rendering* of the three answers, which is this screen's alone.
   *
   * Returns whether an offer is now standing — which the caller needs, because a tile holding a
   * control the resident has to read and press cannot reset on the 1.6-second chip timer. **On
   * native it never stands**, so the short timer is what runs there; that follows from the
   * decision rather than from a second platform test at the timer, and adding one would be the
   * divergence D240 removed.
   */
  async function settleFallback(
    gate: CachedGate,
    pedestrian: boolean,
    verdict: CloudVerdict,
    tappedAt: number,
    plan: TransportPlan,
    /**
     * **D273 — whether this attempt licenses a fallback on a credential it does not hold.** It is
     * threaded rather than derived here for the same reason `plan` is: both are facts about the
     * attempt that has just finished, and re-deciding either at the point of use is the
     * between-lock-and-act read PEC-13 forbids.
     */
    coldFallback: ColdFallbackLicence,
    /** R6 A3 — the tap this fallback belongs to, so its BLE leg is timed from the original tap. */
    timing: OpenTiming,
  ): Promise<{ readonly offered: boolean; readonly outcome?: GateOpenOutcome }> {
    /*
     * **The plan is threaded in rather than recomputed, and PEC-13 is the reason.** Recomputing
     * here would read `hasCredential` *after* the cloud attempt, so a credential that expired
     * during a twenty-second request could change the answer between locking a transport and
     * acting on the lock — which is the in-flight re-evaluation the rule exists to forbid, and it
     * would also put the two screens on different rules for the same decision.
     */
    const decision = decideFallback(plan, verdict, tappedAt, coldFallback)
    if (decision.act === 'nothing') return { offered: false }
    if (decision.act === 'dispatch') {
      // R6 A3 — `nextLeg()`, so this line's `elapsedMs` is the whole span the resident waited,
      // failed cloud leg included. The radio's own cost is the difference against the cloud line.
      return {
        offered: false,
        outcome: await dispatchLocal(gate, pedestrian, decision.verdict, timing.nextLeg()),
      }
    }
    setOpenStates((prev) => ({
      ...prev,
      [gate.gateId]: {
        ...(prev[gate.gateId] ?? IDLE),
        fallbackOffer: { pedestrian, verdict: decision.verdict },
      },
    }))
    return { offered: true }
  }

  async function runCommand(gate: CachedGate, pedestrian: boolean): Promise<void> {
    if (sorting) return
    /*
     * **D295 — a second tap on a gate already in flight is DROPPED, here at dispatch.**
     *
     * D276's clause read *"the lease would refuse the second command anyway and the UI says so
     * first"*, and it rested on D223's own asserting-rather-than-observing premise: *the lease
     * holder's command not having completed when the `409` is issued.* Production disconfirmed it
     * — `gate-contention-handoff.md` §0 has a `gate-busy` landing **3.4 s into a 4.0 s exchange
     * that ended `timeout`**, rendered to the resident as a green tick over a command that was
     * failing at that moment. **The lease does refuse the second command; being refused is the
     * defect.**
     *
     * **D291 set the precedent for WHERE this lives, and this is the opposite of what it does
     * there.** Gate Detail *holds* a tap in a `queuedRef` and dispatches it when the gate read
     * resolves, because that tap is waiting on a **read** rather than competing with a command.
     * Here a queue would put a second command into the gate lease, which is the contention this
     * removes — so the tap is dropped. **Do not "fix" this into a queue.**
     *
     * **At dispatch and not as `disabled` on a control**, because the standard grid's actuator is
     * the whole tile (`wholeTileOpens`, a `<div role="button">`) and cannot carry the attribute at
     * all. **And it mints no visual state**: the tile is already blue while a command is in flight
     * (D228), and D276 refused a `:disabled` appearance for a ~1.6 s window on the app's most
     * guarded control. That half of D276 is kept; only its unguarded-open clause reverses.
     *
     * **It sits above the access and plan refusals deliberately.** Both of those write a `denied`
     * state, so a duplicate tap reaching them would paint a refusal about a **later** condition
     * over a live report of the command already running — an open dispatched over the cloud whose
     * reception then drops reads `plan.primary === 'none'` on the second tap, and would replace
     * *sending* with *offline* on a command still in the air.
     *
     * 🔸 **What it cannot reach, stated rather than hidden.** A collision between two **devices**
     * (3 of the 7 classified on production, §1.4) is state in one phone's memory the other has
     * never heard of; the lease is the only thing that sees both, and it already refuses. And this
     * **cannot survive navigation** — `openStates` is `useState` here, so Home → Gate Detail or any
     * remount loses it, bounded by Gate Detail carrying its own guard (`commandDisabled` includes
     * `phase === 'sending'`).
     */
    if (inFlightRef.current.has(gate.gateId)) return
    const access = accessForCard(gate)
    if (access.reason !== null) {
      setOpenState(gate.gateId, { phase: 'denied', pedestrian, reason: access.reason })
      resetLater(gate.gateId, 1200)
      return
    }

    /*
     * **PEC-13 — the transport is locked here, before dispatch, for this attempt only.**
     *
     * `deviceCache.readOnly` used to return at the top of this function, which is what made an
     * offline tap do **nothing at all**: no beam, no refusal, no explanation. D189(e) replaced
     * that with a decision — a local open where one is possible, and a *named* refusal where it is
     * not. Everything else on Home stays read-only offline; a drag is still a cloud write wearing
     * a gesture, and `commitOrder` refuses on its own.
     */
    // R4/D217 — the **held** question, not the fresh one, paired with the `offline: true`
    // dispatch below. Gate Detail makes the same call; see `PickInput.credentialHeld`. Read once
    // here because D264's branch below asks the same question and must get the same answer:
    // re-reading it after the pick is the between-lock-and-act race PEC-13 forbids.
    const credentialHeld = holdsCredential(gate.gateId)
    const plan = pickTransport({
      online: deviceCache.online,
      bleSupported: bleAdapter().supported(),
      // Unknown ⇒ permitted. `bleConfigured` cannot reach this screen — see the warm effect above.
      blePermitted: true,
      freshness: evaluateFreshness(deviceCache.fetchedAt),
      credentialHeld,
    })
    if (plan.primary === 'none') {
      setOpenState(gate.gateId, { phase: 'denied', pedestrian, bleRefusal: plan.reason })
      resetLater(gate.gateId, 1600)
      return
    }

    /*
     * **D264 — a latched tap with no local path tries the cloud rather than refusing.**
     *
     * `session.online` is a latch that only a successful call clears, and the event that was meant
     * to clear it does not always fire. While it is stuck every tap here is sent to the radio, so
     * a gate holding no credential was refused without the client asking whether the network had
     * returned. Letting the tap through **is** the probe: after hours latched the access token has
     * expired, so `request()` refreshes before it sends and a failed refresh throws rather than
     * proceeding — one deadline, not two, and no separate reachability call.
     *
     * The rule is `attemptsCloudWhileLatched`, shared with `decideFallback` rather than written
     * here, and it keys off `credentialHeld` and **never** off `plan.primary` — see that function
     * for why the plan would invert it.
     */
    const latchedToCloud = attemptsCloudWhileLatched(plan, deviceCache.online, credentialHeld)
    /*
     * **D273 — the plan this attempt is locked to, which is not always the plan that was picked.**
     *
     * A diverted tap makes a **cloud** attempt, so the offline plan `pickTransport` returned
     * describes an attempt that is not being made — and its `fallback: null` bailed
     * `decideFallback` out before either platform branch, which is R6 §2 F12's whole mechanism.
     * Derived once, here, and threaded from here on (PEC-13); `plan` itself is left alone because
     * the refusal branches above still read the reason it carries.
     */
    const attemptPlan = latchedToCloud ? latchedCloudPlan(plan) : plan

    setOpenState(gate.gateId, { phase: 'beaming', pedestrian })
    /*
     * **R6 A3 — the tap is timed from here, which is the same line the overlay is raised past.**
     * Both refusals above send nothing, so neither is a tap this measures — and having the wait
     * animation and the measurement agree about what a tap is means a future change to one cannot
     * silently move the other.
     */
    const timing = beginOpenTiming({ surface: 'home', gateId: gate.gateId })
    // Deliberately after the access precheck above and not before it: that branch refuses locally
    // and sends nothing, so there is no wait to draw and no receipt to answer with. The overlay
    // exists for a command that actually left the device.
    raiseOverlay(gate.gateId)

    // Every path out of the try below is a refusal except the one that isn't, so this defaults to
    // the refusal and `finally` is what delivers it — which is also how the rethrow path (a
    // non-`NetworkError`, which leaves this function by throwing) still dismisses the overlay
    // rather than leaving it running over a screen that has stopped reporting.
    let outcome: GateOpenOutcome = 'refused'
    // Set when a failed cloud attempt leaves an offer standing, so the `finally` below can hold
    // the tile open long enough to read and press it instead of resetting after 1.6 s.
    let offered = false

    if (plan.primary === 'ble' && !latchedToCloud) {
      try {
        outcome = await dispatchLocal(gate, pedestrian, undefined, timing)
      } catch (error) {
        setOpenState(gate.gateId, { phase: 'denied', pedestrian, usedTransport: 'ble', genericFailure: true })
        if (!(error instanceof NetworkError)) throw error
      } finally {
        answerOverlay(gate.gateId, outcome)
        resetLater(gate.gateId, 1600)
      }
      return
    }

    const tappedAt = Date.now()
    let verdict: CloudVerdict | null = null
    /*
     * **D273 — *a response came back*, and it is set on the line after the await for a reason.**
     * `openGate` returning at all is the proof: a `200 outcome: 'failed'`, a `403` and a `409` all
     * reach it, while a `NetworkError`, the client's own `REQUEST_TIMEOUT_MS` and anything that
     * throws leave it `false`. Conservative in the direction that withholds the cold fallback,
     * which is the direction where the fetch would meet the silence the tap was diverted over.
     */
    let cloudAnswered = false
    try {
      const result = await openGate(session.request, gate.gateId, pedestrian)
      cloudAnswered = true
      // A `2xx` carrying no `outcome` is a malformed response, not an open (D135).
      if (result.ok && result.data.outcome === 'opened') {
        outcome = 'opened'
        setOpenState(gate.gateId, { phase: 'opened', pedestrian, usedTransport: 'cloud' })
        timing.settle({ transport: 'cloud', verdict: 'opened', reason: 'delivered' })
        // D163's stamp is exactly this event — `outcome: 'opened'` is the same delivery receipt
        // the server's own audit envelope commits on (D134) — so it renders the moment the
        // "Sent" chip clears rather than waiting on a re-fetch.
        setLastUsedAt((prev) => ({ ...prev, [gate.gateId]: new Date().toISOString() }))
      } else if (result.ok && result.data.outcome === 'failed') {
        // PEC-8: the transport did not confirm, which is **not** proof the gate did nothing — so
        // the local path is offered rather than taken.
        setOpenState(gate.gateId, {
          phase: 'denied',
          pedestrian,
          usedTransport: 'cloud',
          unconfirmed: true,
          ...deliveryFailurePatch(result.data.deliveryOutcome),
        })
        // R6 A3/§2 F2 — the wire carries `outcome: 'failed'` and nothing else, which is exactly
        // the finding O7 exists to fix: five causes arrive here as one word.
        timing.settle({ transport: 'cloud', verdict: 'unconfirmed', reason: 'unconfirmed' })
        verdict = 'ambiguous'
      } else {
        /*
         * **R4/D223 — both `409`s are Success, and neither offers Bluetooth.**
         *
         * *"User get only success and failure. nothing in the middle."* The platform has the
         * command in both cases: `gate-busy` means **an open holds the gate lease** and
         * `open-in-flight` means this caller's own previous tap is still running. Each used to
         * draw its own sentence — and `gate-busy` set `verdict = 'ambiguous'`, so the app offered
         * a **second dispatch against a command that had already landed**, which is PEC-13's
         * forbidden case reached through the wrong door. A Success offers nothing, so both
         * `verdict` assignments are gone rather than merely nulled.
         *
         * **⚠️ `gate-busy` is not *somebody else's* open, which is what this docblock claimed
         * until D295.** Production classified 7 of 8 refusals by source IP and platform, and **4
         * were the same device** — the resident's own earlier tap under a different idempotency
         * key (`gate-contention-handoff.md` §1.4); two devices on one household IP made 2 and two
         * different residents 1. **Any copy written here must therefore not name another person.**
         * D295's dispatch guard is what removes the same-device half; the rest belongs to the
         * lease, which is the only thing that sees both phones. D294 makes the name accurate in the
         * other direction — a *close* holder answers `409 gate-closing` — and is not built.
         *
         * **`lastUsedAt` stays unstamped for the same reason `dispatchLocal` leaves it alone**:
         * neither refusal is a new opening to date. `open-in-flight` is this viewer's earlier tap,
         * which stamps when it returns, and a `gate-busy` holder — whosever it is — stamps for
         * itself on the device that made it.
         *
         * `not-authorized` and every `DenyReason` are untouched — an access refusal is a third
         * thing, and the resident needs its reason in order to act on it. They leave `verdict`
         * null because the server refused the *grant*, and offering Bluetooth would be offering a
         * way around that. Everything unrecognized is still the generic failure.
         */
        const reason = result.reason
        if (reason === 'gate-busy' || reason === 'open-in-flight') {
          outcome = 'opened'
          setOpenState(gate.gateId, { phase: 'opened', pedestrian, usedTransport: 'cloud' })
          timing.settle({ transport: 'cloud', verdict: 'opened', reason })
        } else if (reason === 'gate-closing') {
          /*
           * **D294 — the resident tapped Open and the gate is closing, so this is Failed.**
           *
           * It is the matrix cell D223 got wrong and D297 could not reach: `gate-busy` used to cover
           * both directions, so a tap made while the gate was shutting drew a green tick and the gate
           * kept shutting. The slot and the flag are D272's; only the sentence is new, and it is new
           * because neither existing cause is true here — the gate answered nothing (nothing was
           * asked) and it did not fail to move (it is moving, the other way).
           *
           * **`usedTransport` stays unset** (D264's rule: no transport carried anything) and
           * **`verdict` stays null**, so no Bluetooth offer stands: the holder's close may still be
           * travelling, and a local open would be a second command at a gate already in motion —
           * D297's own PEC-8 argument, reached by a stronger door, since here the direction is known.
           */
          setOpenState(gate.gateId, {
            phase: 'denied',
            pedestrian,
            unconfirmed: true,
            openRefusal: 'gate-closing',
          })
          timing.settle({ transport: 'cloud', verdict: 'unconfirmed', reason })
        } else if (isContendedOpenRefusal(reason)) {
          /*
           * **D297 — the holder's command did not open the gate, and the resident is told so.**
           *
           * `unconfirmed` and the `deliveryFailure` patch are D272's existing machinery reached by a
           * second door: `gate-unreachable` and `gate-not-acting` carry F2's and F3's sentences, and
           * `gate-command-failed` carries the generic one. **No new failure string, no new line, and
           * `metaLines` unchanged** — this is the same slot a `200 outcome: 'failed'` renders in.
           *
           * **`usedTransport` is deliberately unset**, on D264's rule: no transport carried anything
           * for this caller. And **`verdict` stays null**, so no Bluetooth offer stands. D223's
           * reason for that lapses here (these are Failed, not Success) and a different one holds:
           * PEC-8 says the holder's command may have actuated despite answering nothing, so a local
           * dispatch would be a **second** command at a gate that may already be moving — the
           * cross-talk D125's lease exists to prevent and which the radio bypasses. 🔸 Widening the
           * fallback posture here is its own decision (D240/D273 territory) and D297 does not take
           * it; the resident's own next tap still reaches everything it did before.
           */
          setOpenState(gate.gateId, {
            phase: 'denied',
            pedestrian,
            unconfirmed: true,
            ...contendedFailurePatch(reason),
          })
          timing.settle({ transport: 'cloud', verdict: 'unconfirmed', reason })
        } else if (reason === 'not-authorized') {
          setOpenState(gate.gateId, { phase: 'denied', pedestrian, notAuthorized: true })
          timing.settle({ transport: 'cloud', verdict: 'refused', reason })
        } else if (reason === undefined) {
          setOpenState(gate.gateId, { phase: 'denied', pedestrian, genericFailure: true })
          timing.settle({ transport: 'cloud', verdict: 'failed', reason: 'unknown' })
          verdict = 'ambiguous'
        } else {
          setOpenState(gate.gateId, { phase: 'denied', pedestrian, reason })
          timing.settle({ transport: 'cloud', verdict: 'refused', reason })
        }
      }
    } catch (error) {
      // R1-6: the rethrow for a non-`NetworkError` used to happen before this and before the
      // `setTimeout` below, so a tile stuck on "beaming" (`שולח…`) stayed stuck for the life of
      // the screen. State first, then decide whether to rethrow; `finally` covers the reset timer
      // so it fires on every path, thrown or not.
      setOpenState(gate.gateId, {
        phase: 'denied',
        pedestrian,
        genericFailure: true,
        // **D272 F1** — `isNoDataFailure`, never a bare `instanceof NetworkError`: two subclasses
        // inherit from it and neither is a phone with no data. That module carries the whole rule.
        ...(isNoDataFailure(error) ? { noData: true } : {}),
      })
      // D189(b) — a full twenty-second budget means the request *was sent*; a fast rejection means
      // it never left. See Gate Detail's `runCommand` for the whole of the argument.
      verdict =
        error instanceof RequestTimeoutError
          ? 'ambiguous'
          : error instanceof NetworkError
            ? 'never-dispatched'
            : 'ambiguous'
      /*
       * **D264 — the diverted tap keeps the sentence it would have had.** `never-dispatched` means
       * the request never left the device, so nothing has changed since the pick: still latched,
       * still no credential, and still no local path. That state has always read
       * `ble.localNoCredential`, and the generic failure line written above claims less than the
       * client knows — which is `BLE_FAILURE_KEY`'s own rule for this reason, one screen over: *a
       * message may only assert what something actually witnessed.* Without this the fix's only
       * user-visible cost would be a **worse** sentence as well as a slower one.
       *
       * `usedTransport` is deliberately left unset: no transport carried anything.
       */
      if (latchedToCloud && verdict === 'never-dispatched') {
        setOpenState(gate.gateId, {
          phase: 'denied',
          pedestrian,
          bleFailure: 'no-credential',
          cloudVerdict: verdict,
        })
      }
      /*
       * **R6 A3 — this is the line R1 has been missing.** `measurements.md` R1 is an unexplained
       * ~8 s on a failed open that nothing can attribute, and the two candidates it names are both
       * here: a client deadline that ran its full `REQUEST_TIMEOUT_MS` (F6, which O8 would cut) and
       * an automatic BLE leg queued behind it (which `nextLeg()`'s own line then names). The reason
       * is the *classification the screen already made* for D189(b), not a second one.
       */
      timing.settle({
        transport: latchedToCloud && verdict === 'never-dispatched' ? 'none' : 'cloud',
        verdict: 'failed',
        reason:
          error instanceof RequestTimeoutError
            ? 'client-timeout'
            : error instanceof NetworkError
              ? latchedToCloud && verdict === 'never-dispatched'
                ? 'no-credential'
                : 'network'
              : 'threw',
      })
      if (!(error instanceof NetworkError)) {
        const settled = await settleFallback(
          gate,
          pedestrian,
          verdict,
          tappedAt,
          attemptPlan,
          coldFallbackLicence(latchedToCloud, cloudAnswered),
          timing,
        )
        answerOverlay(gate.gateId, settled.outcome ?? outcome)
        resetLater(gate.gateId, settled.offered ? FALLBACK_OFFER_MS : 1600)
        throw error
      }
    }

    if (verdict !== null) {
      const auto = await settleFallback(
        gate,
        pedestrian,
        verdict,
        tappedAt,
        attemptPlan,
        coldFallbackLicence(latchedToCloud, cloudAnswered),
        timing,
      )
      offered = auto.offered
      if (auto.outcome !== undefined) outcome = auto.outcome
    }
    answerOverlay(gate.gateId, outcome)
    resetLater(gate.gateId, offered ? FALLBACK_OFFER_MS : 1600)
  }

  /**
   * **R7/D276 — the Close, and it is a SECOND function rather than a direction on `runCommand`.**
   *
   * `api.ts`'s own argument for `closeGate` beside `openGate`, one layer up: the two routes invert
   * two of D223's four rows, and a direction parameter is what lets a call site pass the wrong one
   * — here silently, on the command that moves a leaf toward a person. Everything the two share is
   * shared by call (`accessForCard`, `setOpenState`, `answerOverlay`, `OpenAnimation`), and what is
   * not shared is exactly what R7 inverted.
   *
   * Five things not to undo.
   *
   * **There is no BLE close (D277 (g)), so this never consults `pickTransport` and never reaches
   * the radio.** A close is a cloud command or nothing — which is also how S6's *if the plan says
   * BLE, the close goes to the cloud* is satisfied: the plan is not asked. Offline the request
   * throws, and D272 F1's own sentence (*no internet connection — the command wasn't sent*) is a
   * better answer than any of `pickTransport`'s three BLE refusals, every one of which names a
   * radio this direction cannot use.
   *
   * **And therefore nothing here reaches `settleFallback` or `decideFallback`** — R7 §6's *do not
   * let a Close ride D240's automatic fallback* (D276 (e)) held by construction rather than by a
   * branch. `verdict` is not computed at all: there is no second transport for one to license.
   *
   * **`lastUsedAt` is deliberately not stamped.** D163's line is *the viewer's own most recent
   * **open***, which is what the tile's copy says (`lastUsed` reads "opened {{time}}"), so a close
   * moving it would date an opening that did not happen — `dispatchLocal`'s own rule for a
   * `refused` and D223's for the two `409`s, reached from the other direction.
   *
   * **`open-in-flight` stays Success and `gate-busy` becomes Failed**, which is D277 (b)'s first
   * inversion and its boundary in one place: `closeFailureKey`'s input union names exactly the two
   * refusals a close re-answers, and `open-in-flight` is not one of them — it is this caller's own
   * previous command still running, so the platform demonstrably has a command for this gate
   * (D223), while `gate-busy`'s holder is somebody **opening** the gate, which is the opposite of
   * the goal rather than a slower route to it.
   *
   * **It is not timed.** `beginOpenTiming` feeds R6 A3's open-latency distribution, and a close
   * carries a different exchange (D277's compound: Stop, a settle, then the Close). Pooling the
   * two makes both unreadable, which is D239's own argument for the `Exchange` dimension one
   * transport down. The close's own instrument is a later session's.
   */
  async function runClose(gate: CachedGate): Promise<void> {
    if (sorting) return
    const access = accessForCard(gate)
    if (access.reason !== null) {
      setOpenState(gate.gateId, {
        phase: 'denied',
        pedestrian: false,
        direction: 'close',
        reason: access.reason,
      })
      resetLater(gate.gateId, 1200)
      return
    }

    setOpenState(gate.gateId, { phase: 'beaming', pedestrian: false, direction: 'close' })
    // D276 (g), amended at the owner: a Close raises the SAME overlay with BOTH beats. The beat is
    // a delivery receipt (D134) and never a claim the gate is shut — which is why the refusal above
    // returns before this line, exactly as `runCommand`'s does: a command refused before it is sent
    // has no wait to draw. 🔸 `gate`'s leaves still swing OPEN for a close; that is D276's own owed
    // work on an existing style rather than a new one (D215/D218 does not reach a direction).
    raiseOverlay(gate.gateId)

    // The refusal is the default and `finally` is what delivers it, so the rethrow path below
    // still dismisses the overlay rather than leaving it running over a screen that has stopped
    // reporting — `runCommand`'s own structure, and R1-6's finding.
    let outcome: GateOpenOutcome = 'refused'
    try {
      const result = await closeGate(session.request, gate.gateId, false)
      if (result.ok && result.data.outcome === 'opened') {
        // `toCloseResult` answers `'opened'` for `acknowledged` and `gate-acting` and nothing
        // else, so the Success set is the server's and this reads it rather than re-deriving it.
        outcome = 'opened'
        setOpenState(gate.gateId, {
          phase: 'opened',
          pedestrian: false,
          direction: 'close',
          usedTransport: 'cloud',
        })
      } else if (result.ok && result.data.outcome === 'failed') {
        setOpenState(gate.gateId, {
          phase: 'denied',
          pedestrian: false,
          direction: 'close',
          closeFailed: true,
          ...closeFailurePatch(result.data.deliveryOutcome),
        })
      } else if (result.reason === 'open-in-flight' || result.reason === 'gate-closing') {
        /*
         * **D294 — `gate-closing` is this direction's Success, and R7 S1's C6 is what licenses it.**
         *
         * A Close refused by another **close** is the second cell D294 fixes, and it is the one place
         * D223's Success argument holds for this direction: C6 (n=3) measured a Close onto a *closing*
         * leaf as **ignored, with the close completing** — no `CMD:Err`, none ever observed on a Close
         * — so the holder's command meets this caller's goal exactly as a second Open meets an
         * opener's. `gate-busy` below is untouched and stays Failed: there the holder is **opening**
         * the gate, which is the opposite of met (D277 (b)).
         *
         * **It earns no sentence of its own**, which is the difference from the open path's branch:
         * the goal is met, and a Success that explains itself is a Success the resident has to read.
         * It shares `open-in-flight`'s beat because both mean *the platform is closing this gate*.
         */
        outcome = 'opened'
        setOpenState(gate.gateId, {
          phase: 'opened',
          pedestrian: false,
          direction: 'close',
          usedTransport: 'cloud',
        })
      } else if (result.reason === 'gate-busy' || result.reason === 'close-not-supported') {
        setOpenState(gate.gateId, {
          phase: 'denied',
          pedestrian: false,
          direction: 'close',
          closeFailed: true,
          closeRefusal: result.reason,
        })
      } else if (result.reason === 'not-authorized') {
        // An access refusal is a third thing on this path too (D223), and it keeps its own line.
        setOpenState(gate.gateId, {
          phase: 'denied',
          pedestrian: false,
          direction: 'close',
          notAuthorized: true,
        })
      } else if (result.reason === undefined) {
        setOpenState(gate.gateId, {
          phase: 'denied',
          pedestrian: false,
          direction: 'close',
          closeFailed: true,
        })
      } else {
        // What remains is a `DenyReason` — the same `else` `api.ts` says the open path's refusal
        // chain rests on, which is why `close-not-supported` earned its own union rather than
        // widening `OpenRefusal`.
        setOpenState(gate.gateId, {
          phase: 'denied',
          pedestrian: false,
          direction: 'close',
          reason: result.reason,
        })
      }
    } catch (error) {
      setOpenState(
        gate.gateId,
        // **D272 F1, and the one open-named key a close may borrow.** `openNoData` reads *no
        // internet connection — the command wasn't sent*: it names the COMMAND rather than the
        // direction, in all five catalogs, so it is the honest sentence here and adding a
        // `closeNoData` would be five strings that agree until one is retranslated (D255).
        // Everything else falls to the close's own generic line, which claims only that the
        // command was sent and unconfirmed — never that the gate did not open.
        isNoDataFailure(error)
          ? { phase: 'denied', pedestrian: false, direction: 'close', genericFailure: true, noData: true }
          : { phase: 'denied', pedestrian: false, direction: 'close', closeFailed: true },
      )
      if (!(error instanceof NetworkError)) throw error
    } finally {
      answerOverlay(gate.gateId, outcome)
      resetLater(gate.gateId, 1600)
    }
  }

  /**
   * **Every one of these writes could fail, and none of them used to be read.**
   *
   * The results were discarded and there was no `catch`, so two things happened. Offline,
   * `session.request` throws `NetworkError` — inside a `Promise.all`, from a `void commitOrder(…)`
   * with nobody awaiting it, which is an unhandled rejection rather than a message. And a refused
   * write left the tiles sitting in their new order on screen while the server kept the old one,
   * so the reorder silently undid itself at the next refresh — the user's own next launch, with no
   * connection between cause and effect.
   *
   * Reordering is also now refused offline at the entry point below, the way `runCommand` already
   * refuses an open: `device-cache.md`'s offline rule is read-only, and a drag is a cloud write
   * wearing a gesture. This stays as the backstop for reception lost mid-reorder.
   */
  async function commitOrder(next: readonly string[]): Promise<void> {
    setOrder(next)
    setReorderFailed(false)
    try {
      const results = await Promise.all(
        next.map((gateId, index) => patchView(session.request, gateId, { sortOrder: index })),
      )
      // A partial failure is still a failure: the order the server holds is now neither the old
      // one nor the one on screen, and the next refresh is what will reveal it.
      if (results.some((result) => !result.ok)) {
        setReorderFailed(true)
        return
      }
      // Only on a clean write: the device-cache snapshot is the source the *next launch* restores
      // from, and it is otherwise never told about a reorder. Writing it only here — never on a
      // partial or failed commit — keeps it agreeing with whatever the server actually holds.
      await Promise.all(next.map((gateId, index) => deviceCache.updateGateView(gateId, { sortOrder: index })))
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setReorderFailed(true)
    }
  }

  function moveBy(gateId: string, delta: number): void {
    const index = order.indexOf(gateId)
    const target = index + delta
    if (index < 0 || target < 0 || target >= order.length) return
    const next = [...order]
    const [moved] = next.splice(index, 1)
    if (moved === undefined) return
    next.splice(target, 0, moved)
    void commitOrder(next)
  }

  function moveTo(gateId: string, targetGateId: string): void {
    if (targetGateId === gateId) return
    const next = [...orderRef.current]
    const from = next.indexOf(gateId)
    const to = next.indexOf(targetGateId)
    if (from < 0 || to < 0) return
    next.splice(from, 1)
    next.splice(to, 0, gateId)
    setOrder(next)
  }

  /**
   * Pointer capture is set by the caller, not here — `GateCard` needs the same call from two
   * places (a live `pointerdown` once already in sort mode, and a still-down pointer whose
   * long-press timer just fired) and only one of those has a fresh `PointerEvent` to read
   * `currentTarget`/`pointerId` off.
   */
  function startPointerDrag(gateId: string): void {
    pointerDrag.current = { gateId, original: orderRef.current }
  }

  function movePointerDrag(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = pointerDrag.current
    if (drag === undefined) return
    const point = { x: event.clientX, y: event.clientY }
    const entries = [...cardRefs.current.entries()]
    // A straight vertical line only tells rows apart, never columns — fine for the single-column
    // list layout, but A · Keypad's grid puts two tiles on every row, so a Y-only test always
    // resolved to whichever tile came first in DOM order for that row (in this RTL app, the
    // visually-*right* one), no matter where the pointer actually was. Dragging into the other
    // column read as "does nothing": if that first tile was the one already being dragged, its
    // own row could never resolve to its neighbor. `x` has to be part of the test too.
    let target = entries.find(([, element]) => {
      const rect = element.getBoundingClientRect()
      return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom
    })?.[0]
    // Between tiles (a gap, or past the last one in a row/column) the point lands inside no
    // rect at all — nearest center, rather than leaving the drag stuck on the last exact hit.
    if (target === undefined) {
      let bestDistance = Number.POSITIVE_INFINITY
      for (const [gateId, element] of entries) {
        const rect = element.getBoundingClientRect()
        const distance = Math.hypot(
          point.x - (rect.left + rect.width / 2),
          point.y - (rect.top + rect.height / 2),
        )
        if (distance < bestDistance) {
          bestDistance = distance
          target = gateId
        }
      }
    }
    if (target !== undefined) moveTo(drag.gateId, target)
  }

  function finishPointerDrag(cancelled: boolean): void {
    const drag = pointerDrag.current
    pointerDrag.current = undefined
    if (drag === undefined) return
    if (cancelled) {
      setOrder(drag.original)
      return
    }
    if (orderRef.current.some((id, index) => id !== drag.original[index])) void commitOrder(orderRef.current)
  }

  return (
    <>
      <AppBar
        title={t('title')}
        onMenu={onMenu}
        {...(menuButtonRef === undefined ? {} : { menuButtonRef })}
        {...(sorting
          ? {
              sort: {
                onDone: () => {
                  setSorting(false)
                  setPickedUp(undefined)
                },
              },
            }
          : {
              layout: {
                grid,
                spacious,
                // The whole mode, never a patch: the three positions are a cycle
                // (`layoutPreference.ts`), so advancing one boolean at a time is what would let
                // the removed large *list* back in.
                onCycle: () => updateLayout(nextHomeLayout(layoutRef.current)),
              },
            })}
      />
      {/*
        D275's fallback, and it is deliberately the first thing under the bar rather than a layer
        over the list: the install is already going to happen on the next background, so this asks
        rather than interrupts. It is absent for a muted resident — `installReady` folds D266 §3.5
        in — and absent on every rail but Android's, no other reaching a `downloaded` phase.

        **D331 shares this exact slot with D275's banner, and the two are mutually exclusive by
        rule: D275 always wins.** `UpdateReadyBanner` is un-dismissable by decision (its own
        docblock) and ties to bytes already on the device waiting to install — silencing it would
        be the one thing this session was told not to do. The operator notice has no device state
        behind it and nothing that expires it, so it simply waits: the `<div>` below is D331's
        entire footprint here — a callback ref registering the slot into `homeNoticeHost.ts`
        whenever D275's banner is NOT showing, so `HomeNoticePortal` (mounted outside this
        screen's own closure, `main.tsx`) paints into it exactly when there is nothing else to
        show. When `updateInstallReady` flips true, React unmounts this div, the ref fires with
        `null`, and the notice — if any — disappears from Home along with its slot; it returns on
        the next render where the slot is free again. Nothing is lost, only deferred, and two
        full-width blocks can never stack here.
      */}
      {updateInstallReady ? (
        <UpdateReadyBanner onRestart={applyUpdate} />
      ) : (
        <div ref={registerHomeNoticeHost} />
      )}
      {/*
        Rendered outside the reorder mode that produced it: the writes settle after the user has
        already tapped "done", and a message that vanished with the mode would be a message nobody
        ever saw. It clears on the next successful reorder.
      */}
      {reorderFailed ? (
        <div className="roster-notice">
          <p className="hint error">{t('reorderFailed')}</p>
        </div>
      ) : null}
      {/*
        D32 — always present once there is something to search, unobtrusive until focused; one
        tap clears it. Absent only while the list itself is still loading or empty, so a control
        the screen cannot yet honour never appears (the same reasoning as D87's reserved states).
        A8 (`ui-scale-refresh-handoff.md`) — it survives entering sort mode rather than
        disappearing: `onLongPress` already clears the query on entry (so a drag never starts on
        a filtered subset), and hiding the control on top of that only cost the resident the
        ability to search *while* reordering, which reordering does not need to forbid.
      */}
      {orderedGates.length > 0 ? (
        <div className="search-bar">
          <Icon name="search" size={15} />
          <input
            type="text"
            className="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('searchPlaceholder')}
            aria-label={t('searchPlaceholder')}
            autoComplete="off"
            spellCheck={false}
          />
          {query !== '' ? (
            <button
              type="button"
              className="search-clear"
              aria-label={t('clearSearchLabel')}
              onClick={() => setQuery('')}
            >
              <Icon name="x" size={14} />
            </button>
          ) : null}
        </div>
      ) : null}
      {orderedGates.length === 0 &&
      deviceCache.fetchedAt === undefined &&
      deviceCache.lastRefreshFailed === true ? (
        /**
         * **R1-8 — the lie that survived the mid-fetch guard below: a failed refresh with no
         * snapshot.** A cold launch offline, and any `GET /gates` 5xx, both used to fall through
         * to "אין עדיין שערים" — and a 5xx leaves `deviceCache.online` `true`, so there was not
         * even an offline banner to contradict it. `fetchedAt` is *undefined* only until this
         * launch's list has ever landed for this account, and `lastRefreshFailed` is what tells
         * "asked and failed" apart from "never asked" below — a paying resident is never told
         * they own no gates on the strength of a request that never got an answer.
         */
        <div style={{ padding: '48px 20px', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--font-cardTitle)', marginBottom: 6 }}>
            {t('loadFailedTitle')}
          </div>
          <div style={{ fontSize: 'var(--font-control)', color: 'var(--muted)', marginBottom: 16 }}>
            {t('loadFailedBody')}
          </div>
          <SecondaryButton onClick={() => void deviceCache.refreshAll()}>{tCommon('retry')}</SecondaryButton>
        </div>
      ) : orderedGates.length === 0 && deviceCache.fetchedAt === undefined ? (
        /**
         * **"You have no gates" is a claim, and before the list has ever landed it is one we
         * cannot make.** Covers both the mid-fetch case (an install race — the app bar's spinner
         * covers the case where there is already a list to spin beside) and the rarer
         * not-yet-asked one; neither has failed, so this is loading copy rather than the retry
         * state above.
         */
        <div
          style={{
            padding: '48px 20px',
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: 'var(--font-control)',
          }}
        >
          {tCommon('loading')}
        </div>
      ) : orderedGates.length === 0 ? (
        // `fetchedAt` is set: this launch's `GET /gates` has actually landed and came back
        // empty. The only one of the three branches that means *you have no gates*.
        <div style={{ padding: '48px 20px', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--font-cardTitle)', marginBottom: 6 }}>
            {t('emptyTitle')}
          </div>
          <div style={{ fontSize: 'var(--font-control)', color: 'var(--muted)' }}>{t('emptyBody')}</div>
        </div>
      ) : visibleGates.length === 0 ? (
        // The viewer does have gates; the search simply matched none of them. Distinct from
        // `emptyTitle` above, which claims the account itself owns nothing.
        <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--muted)' }}>
          <p className="hint centered">{t('noSearchResults')}</p>
        </div>
      ) : (
        <div className={`home-list${grid ? ' grid' : ''}${spacious ? ' spacious' : ''}`}>
          {visibleGates.map((gate) => {
            const lastUsedText = lastUsedTextFor(gate.gateId, lastUsedAt, locale, t)
            return (
              <GateCard
                key={gate.gateId}
                gate={gate}
                grid={grid}
                spacious={spacious}
                sorting={sorting}
                pickedUp={pickedUp === gate.gateId}
                openState={openStates[gate.gateId] ?? IDLE}
                {...(lastUsedText === undefined ? {} : { lastUsedText })}
                dir={dir}
                openLabel={t('openGateLabel', { name: gate.alias ?? gate.defaultName })}
                closeLabel={t('closeGateLabel', { name: gate.alias ?? gate.defaultName })}
                settingsLabel={t('settingsLabel', { name: gate.alias ?? gate.defaultName })}
                reorderLabel={t('reorderLabel', { name: gate.alias ?? gate.defaultName })}
                openingText={t('sending')}
                openedText={t('sent')}
                pedestrianOpenedText={t('pedestrianSent')}
                failedText={t(openStates[gate.gateId]?.noData === true ? 'openNoData' : 'openFailed')}
                notAuthorizedText={t('openNotAuthorized')}
                /* D294 — `openFailureKey` rather than `deliveryFailureKey`, because a close holding
                   the lease is a reason and not a delivery outcome. It answers for an idle tile too
                   (its `undefined` row is today's generic line); only `unconfirmed` decides whether
                   the sentence is drawn. */
                unconfirmedText={t(
                  openFailureKey({
                    refusal: openStates[gate.gateId]?.openRefusal,
                    failure: openStates[gate.gateId]?.deliveryFailure,
                  }),
                )}
                /* R7/D277 — resolved here like every other string the card renders, so the card
                   still holds no `t`. `closeFailureKey` answers for an idle tile too (its
                   `undefined` row is the generic close line), exactly as `openFailureKey`
                   above does; only `closeFailed` decides whether it is drawn. */
                closeFailedText={t(
                  closeFailureKey({
                    refusal: openStates[gate.gateId]?.closeRefusal,
                    failure: openStates[gate.gateId]?.closeFailure,
                  }),
                )}
                sentLocallyText={t('sentLocally')}
                fallbackPromptText={tBle('tapAgainForBluetooth')}
                cloudFailedText={tBle('cloudOpenFailed')}
                fallbackWarningText={tBle('fallbackAfterUnconfirmed')}
                openOverBluetoothLabel={t('openOverBluetoothLabel', { name: gate.alias ?? gate.defaultName })}
                {...bleLineTextFor(openStates[gate.gateId] ?? IDLE, tBle)}
                onOpen={() => {
                  // **The tile IS the fallback control.** D189(b) put a second button on the card;
                  // it is gone, and a standing offer is answered by the same tap that made the
                  // failed attempt — the tile's own name line is what says so. Resolved here
                  // rather than inside the card because the offer lives in `openStates`, of which
                  // the card receives one slice.
                  const offer = (openStates[gate.gateId] ?? IDLE).fallbackOffer
                  if (offer === undefined) {
                    void runCommand(gate, false)
                    return
                  }
                  // The offer comes off the tile before the exchange rather than after it — the
                  // BLE round trip is seconds long, and an invitation still reading "tap again"
                  // over a dispatch already running invites the second dispatch PEC-13 forbids.
                  // `dispatchLocal` writes that `beaming` state itself now (R4 §7.1b), so every
                  // caller announces its start the same way and this one states nothing twice.
                  // R6 A3 — the resident's own second gesture is its own tap, with its own
                  // `tapId`: the wait it measures starts here, not at the tap that failed.
                  void dispatchLocal(
                    gate,
                    offer.pedestrian,
                    offer.verdict,
                    beginOpenTiming({ surface: 'home', gateId: gate.gateId }),
                  )
                }}
                // R7/D276 — the close-on-tap gate's second plate. It carries no offer branch: a
                // Close has no BLE leaf to fall back to (D277 (g)), so there is nothing for a
                // standing invitation to invite.
                onClose={() => void runClose(gate)}
                onSettings={() => navigate.navigate({ id: 'gate', gateId: gate.gateId as GateId })}
                // Offline is read-only (`device-cache.md`), and reordering is a cloud write per
                // gate — the same rule `runCommand` applies to an open, applied to the gesture that
                // starts one. Refusing entry beats letting the drag succeed on screen and fail
                // behind it.
                onLongPress={() => {
                  if (!deviceCache.readOnly) {
                    setQuery('')
                    setSorting(true)
                  }
                }}
                onPickUp={() => setPickedUp((current) => (current === gate.gateId ? undefined : gate.gateId))}
                onMoveUp={() => moveBy(gate.gateId, -1)}
                onMoveDown={() => moveBy(gate.gateId, 1)}
                cardRef={(element) => {
                  if (element === null) cardRefs.current.delete(gate.gateId)
                  else cardRefs.current.set(gate.gateId, element)
                }}
                onPointerDragStart={() => startPointerDrag(gate.gateId)}
                onPointerDragMove={movePointerDrag}
                onPointerDragEnd={() => finishPointerDrag(false)}
                onPointerDragCancel={() => finishPointerDrag(true)}
              />
            )
          })}
        </div>
      )}
      {/* Discoverability for D113's long-press entry — shown only while there is something to
          sort and nothing already explains the gesture (sorting mode has its own "done" chrome
          in the app bar). */}
      {visibleGates.length > 0 && !sorting ? (
        <p className="hint centered reorder-hint">
          <Icon name="arrow-up-down" size={14} />
          <span>{t('reorderHint')}</span>
        </p>
      ) : null}
      {/* Through `FrameOverlay` rather than in place: `.app-screen` is `position: relative` *and*
          the scroll container, so an `inset: 0` layer rendered here would resolve against the
          scrolled content box and the gate would sit wherever the list happened to be.

          **`key={overlay.seq}` is load-bearing and the sentence it replaces was the defect's
          other half.** That sentence read *no `key` is needed — `runCommand` mounts one only
          while `overlay` is `undefined`, so every open gets a fresh instance by construction*,
          and it was true of a raise that refused to supersede an answered overlay. `raiseOverlay`
          now does supersede one, so freshness is no longer a construction: without the `key`
          React reuses the mounted instance, whose `mountedAt`, `answered` and `leaving` belong to
          the open that just finished — the new command would re-enter mid-exit, already swung.
          The old instance's timers are cleared by its own unmount effect, so nothing it scheduled
          can reach through and dismiss the replacement. One overlay is mounted at any instant
          either way, which is D198's one `sg-` filter-id set. */}
      {overlay === undefined ? null : (
        <FrameOverlay>
          <OpenAnimation
            key={overlay.seq}
            style={overlay.style}
            outcome={overlay.outcome}
            /* Seq-guarded rather than a bare `setOverlay(undefined)`: the superseded instance
               is unmounted in the same commit that mounts the replacement, but a `done` timer
               that fires in the gap between `raiseOverlay`'s update and that commit would
               otherwise dismiss the overlay the NEW command just raised — this defect arriving
               back through its own fix, and silent, because a wiped overlay looks exactly like
               one that was never raised. */
            onDone={() => setOverlay((prev) => (prev?.seq === overlay.seq ? undefined : prev))}
          />
        </FrameOverlay>
      )}
    </>
  )
}

const TYPE_ACCENT: Readonly<
  Record<'barrier' | 'door' | 'parking' | 'garage', { readonly bg: string; readonly fg: string }>
> = {
  barrier: { bg: 'var(--acc-barrier-bg)', fg: 'var(--acc-barrier)' },
  door: { bg: 'var(--acc-door-bg)', fg: 'var(--acc-door)' },
  parking: { bg: 'var(--acc-parking-bg)', fg: 'var(--acc-parking)' },
  garage: { bg: 'var(--acc-car-bg)', fg: 'var(--acc-car)' },
}

/**
 * F's wash and A's tile/glyph/gear-ghost all key off the same accent as the icon chip
 * (home-layout-handoff.md §3/§4/§6) — personalization wins over the gate type, and its surface
 * stays a ~13% tint (`22` alpha) behind the glyph's full-strength colour, never a full-strength
 * fill.
 *
 * **Both branches now return CSS variables, and the personalized one used to return hexes.** The
 * `22` above is still the alpha; it is applied in `cssVariables()` where the ramp is generated,
 * because the ramp is per-scheme and a hex resolved here would be frozen to whichever scheme was
 * live when this module loaded.
 */
function accentFor(gate: CachedGate): { readonly bg: string; readonly fg: string } {
  if (gate.color !== undefined && isViewColor(gate.color)) {
    return { bg: personalizationWash(gate.color), fg: personalizationColor(gate.color) }
  }
  const key =
    gate.type === 'garage'
      ? 'garage'
      : gate.type === 'parking'
        ? 'parking'
        : gate.type === 'door'
          ? 'door'
          : 'barrier'
  return TYPE_ACCENT[key]
}

function GateCard({
  gate,
  grid,
  spacious,
  sorting,
  pickedUp,
  openState,
  lastUsedText,
  dir,
  openLabel,
  closeLabel,
  settingsLabel,
  reorderLabel,
  openingText,
  openedText,
  pedestrianOpenedText,
  failedText,
  notAuthorizedText,
  unconfirmedText,
  closeFailedText,
  sentLocallyText,
  fallbackPromptText,
  cloudFailedText,
  fallbackWarningText,
  openOverBluetoothLabel,
  bleLineText,
  onOpen,
  onClose,
  onSettings,
  onLongPress,
  onPickUp,
  onMoveUp,
  onMoveDown,
  cardRef,
  onPointerDragStart,
  onPointerDragMove,
  onPointerDragEnd,
  onPointerDragCancel,
}: {
  readonly gate: CachedGate
  readonly grid: boolean
  /** Only meaningful while `grid` is true — the large/spacious grid, "layout-template". */
  readonly spacious: boolean
  readonly sorting: boolean
  readonly pickedUp: boolean
  readonly openState: OpenState
  /** D104/D121 — undefined renders nothing; present only for a non-manager with a fetched value. */
  readonly lastUsedText?: string
  readonly dir: 'rtl' | 'ltr'
  readonly openLabel: string
  /** R7/D276 — `home.closeGateLabel` (*סגירת {{name}}*), built exactly as `openLabel` is. */
  readonly closeLabel: string
  readonly settingsLabel: string
  readonly reorderLabel: string
  readonly openingText: string
  readonly openedText: string
  readonly pedestrianOpenedText: string
  readonly failedText: string
  /** R1-7(d) — the named access refusal, rendered beside `failedText`. Its two `409` neighbours
   * left with R4/D223, which routes both to the `opened` phase instead of to a sentence. */
  readonly notAuthorizedText: string
  /** PEC-8 — the transport could not confirm, which is not a gate that did not open. */
  readonly unconfirmedText: string
  /**
   * **The one sentence a Failed CLOSE says**, already resolved — `closeFailureKey`'s answer for
   * this tile's state. It is a second prop rather than a second value on `unconfirmedText`
   * because the two vocabularies disagree on two rows (R7/D277 (b)): `gate-busy` is Success for
   * an open and Failed for a close, and `rejected` is the reverse.
   */
  readonly closeFailedText: string
  /** §6's result chip for a command BLE carried (D189). */
  readonly sentLocallyText: string
  /**
   * **What the gate's own name is replaced by while a local fallback is on offer** — *"tap again
   * to open over Bluetooth"*. The tile is the control, so the invitation belongs on the line the
   * eye already reads, not beside it on a second button.
   */
  readonly fallbackPromptText: string
  /** The sub-line beneath it: the cloud attempt that failed, in one short sentence. */
  readonly cloudFailedText: string
  /**
   * PEC-8's warning — *"the gate may already have the command"*, D189(b)'s precondition for an
   * ambiguous second dispatch. **Held rather than rendered on the tile**: three wrapped lines
   * under a two-line invitation is the wall of text this compact offer replaced, and Gate
   * Detail — where the same offer is a full-width button with room above it — still prints it.
   * The tile's own claim is narrower and survives without it: *this tap opens over Bluetooth*.
   */
  readonly fallbackWarningText: string
  /** The card's own `aria-label` while that offer stands — the tap does something else now. */
  readonly openOverBluetoothLabel: string
  /**
   * The one BLE sentence this tile has to say, already resolved.
   *
   * **Resolved by the parent rather than looked up here**, because every other string on this
   * component arrives the same way — the card holds no `t`, so it cannot drift from the screen's
   * own namespace choices, and a key that does not exist fails at the one place that knows both
   * catalogs. A refusal and a failure are mutually exclusive on one tile, so this is one prop.
   */
  readonly bleLineText?: string
  onOpen(): void
  /** R7/D276 — the large grid's second plate, on a close-on-tap gate only. */
  onClose(): void
  onSettings(): void
  onLongPress(): void
  onPickUp(): void
  onMoveUp(): void
  onMoveDown(): void
  readonly cardRef: Ref<HTMLDivElement>
  onPointerDragStart(): void
  onPointerDragMove(event: ReactPointerEvent<HTMLDivElement>): void
  onPointerDragEnd(): void
  onPointerDragCancel(): void
}): JSX.Element {
  const access = accessForCard(gate)
  const manager = gate.viewerRole === 'manager'
  const name = gate.alias ?? gate.defaultName
  const accent = accentFor(gate)
  const iconName =
    gate.icon !== undefined ? (renderNameForViewIcon(gate.icon) as IconName) : iconForGateType(gate.type)
  /**
   * **The large grid's open control is two elements, not one — `.card-corner-button` (transparent,
   * carries the icon) over `.card::before` (the identity plate, a pseudo-element of `.card`
   * itself).** CSS `:active` on the button cannot reach a sibling's `::before`, and reaching it
   * with `:has()` is the exact selector D192 found iOS Safari not re-evaluating on this component
   * — so the plate has to hear about a press from React state instead. List's `.card-gear` needs
   * none of this: it is one element carrying both the fill and the icon, so its own `:active`
   * already moves the whole thing.
   */
  const [controlPressed, setControlPressed] = useState(false)
  const holdTimer = useRef<ReturnType<typeof setTimeout>>()
  const holdOrigin = useRef<{ readonly x: number; readonly y: number }>()
  /**
   * The pointer that is currently counting down to a long-press, captured as plain values
   * (never the `PointerEvent` itself) so the pending timer callback — which runs outside any
   * React event, well after the event that scheduled it could safely be read — can still reach
   * the element and pointer id a real touch is still holding down.
   */
  const pendingDrag = useRef<{ readonly pointerId: number; readonly target: HTMLDivElement }>()

  function cancelHold(): void {
    if (holdTimer.current !== undefined) clearTimeout(holdTimer.current)
    holdTimer.current = undefined
    pendingDrag.current = undefined
  }

  /**
   * **The whole tile is the grip, not a corner of it — and the press that enters sort mode is
   * the same press that starts moving it.** Before sort mode, this is D113's long-press timer;
   * the pointer is still physically down when that timer fires, so rather than wait for a
   * release-then-repress, the timeout callback below retroactively claims that same pointer —
   * `setPointerCapture` with the id/element pinned at `pointerdown`, then `onPointerDragStart()`
   * — so the very next native `pointermove` (now routed by the `sorting` branch below, once
   * React has flushed the state the timer just set) already drags the tile.
   */
  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (sorting) {
      event.currentTarget.setPointerCapture(event.pointerId)
      onPointerDragStart()
      return
    }
    holdOrigin.current = { x: event.clientX, y: event.clientY }
    pendingDrag.current = { pointerId: event.pointerId, target: event.currentTarget }
    holdTimer.current = setTimeout(() => {
      holdTimer.current = undefined
      onLongPress()
      const pending = pendingDrag.current
      pendingDrag.current = undefined
      if (pending === undefined) return
      pending.target.setPointerCapture(pending.pointerId)
      onPointerDragStart()
    }, 500)
  }
  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    if (sorting) {
      onPointerDragMove(event)
      return
    }
    const origin = holdOrigin.current
    if (origin === undefined || holdTimer.current === undefined) return
    const moved = Math.hypot(event.clientX - origin.x, event.clientY - origin.y)
    if (moved > 8) cancelHold()
  }
  function onPointerUp(): void {
    if (sorting) {
      onPointerDragEnd()
      return
    }
    cancelHold()
  }
  function onPointerCancel(): void {
    if (sorting) {
      onPointerDragCancel()
      return
    }
    cancelHold()
  }
  /**
   * Cancels only the pre-sort hold timer — **never** an active drag. A captured pointer's `move`
   * and `up` keep targeting this element while the finger visually strays over a sibling tile
   * (the whole point of the drag), but some browsers still fire `pointerleave` on the capturing
   * element as the cursor crosses its bounds; wiring this to `onPointerDragEnd()` the way the old
   * grip-only handlers implicitly avoided (by never spanning more than one tile) would end the
   * drag the instant it passed over the next card.
   */
  function onHoldPointerLeave(): void {
    if (sorting) return
    cancelHold()
  }

  const commandClass =
    openState.phase === 'idle'
      ? ''
      : ` ${openState.phase === 'beaming' ? 'beaming' : openState.phase === 'opened' ? 'opened' : 'denied'}`
  const latchedClass = access.latched ? ' latched' : ''
  const greyedClass = access.reason !== null && !access.latched ? ' greyed' : ''
  const controlPressedClass = controlPressed ? ' control-pressed' : ''
  /**
   * C3 — tints the whole tile when the subscription is running low. Never on top of `.greyed`:
   * an access block (an expired grant or subscription) already owns that tile, and
   * `renewalDaysRemaining()` returning `null` once expired means the two states cannot overlap
   * by construction, not merely by this guard. `SUBSCRIPTION_LOW_DAYS` is the one constant B2's
   * badge reads too, so the tile and the badge can never disagree about what "low" means.
   */
  const subscriptionDays =
    gate.subscriptionRenewsAt === undefined ? null : renewalDaysRemaining(gate.subscriptionRenewsAt)
  const subLowClass =
    greyedClass === '' && subscriptionDays !== null && subscriptionDays < SUBSCRIPTION_LOW_DAYS
      ? ' sub-low'
      : ''

  // The tap does something else while an offer stands, so the label has to say so — the screen
  // reader's user gets the same sentence the name line now shows a sighted one.
  const label = sorting
    ? reorderLabel
    : openState.fallbackOffer !== undefined
      ? openOverBluetoothLabel
      : openLabel

  /**
   * **List and the large/spacious grid ("layout-template") swap the two icons' positions.**
   * Settings moves into the spot the gate-type glyph used to occupy; the gate-type glyph moves
   * into the spot settings used to occupy. Each keeps its own action — only the location moved.
   * The standard grid ("layout-grid") swaps the same two positions in CSS alone (`global.css`),
   * with neither icon's action following it: the glyph stays decorative and the gear stays the
   * only button, so `wholeTileOpens` alone still opens the gate there. `swapped` therefore stays
   * false for the standard grid — it governs which element JSX renders as a button, not where
   * either icon is drawn.
   */
  const swapped = !grid || spacious
  const wholeTileOpens = grid && !spacious
  /**
   * **R7/D276 — where the Close is offered, resolved once.** `closeModeOf` is the ONE place the
   * `auto` default lives (D279), so this reads it rather than testing the field: an absent
   * `closeMode`, an unrecognised one, and a gate whose snapshot predates the field all render the
   * open-only tile every gate has today.
   *
   * **The layout terms are load-bearing even though the JSX below also gates them**, and the two
   * guards answer different edits: the branch below stops a Close reaching the standard grid,
   * which renders no corner control at all, while `grid && spacious` is the ONLY thing stopping
   * one on the **list** — `swapped` is true there too, so a `{closeButton}` moved beside
   * `{openButton}` in that branch would put a second puck inside D180's bite. Neither mutation is
   * visible to a test on its own, which is what a double guard costs and what it buys.
   */
  const hasClose = grid && spacious && closeModeOf(gate) === 'command'
  /** A close in flight, which is the only thing that disables the Open plate (see below). */
  const closingInFlight = openState.phase === 'beaming' && openState.direction === 'close'

  // The grip icon is a visual affordance only — the whole card is the drag surface (its own
  // `onPointerDown`/`onPointerMove`/`onPointerUp`/`onPointerCancel` below do the work), so this
  // carries no handlers of its own and is hidden from the accessibility tree rather than
  // duplicating the card's own `aria-label={reorderLabel}` on a second focusable control.
  const settingsClass = !grid ? 'icon icon-gear-slot' : swapped ? 'card-glyph-button' : 'card-gear-ghost'
  const settingsButton = sorting ? (
    <button className={`${settingsClass} reorder-handle`} type="button" tabIndex={-1} aria-hidden="true">
      <Icon name="grip" size={grid ? 16 : 22} className={grid ? 'gear-ghost-icon' : ''} />
    </button>
  ) : (
    <button
      className={settingsClass}
      type="button"
      aria-label={settingsLabel}
      onClick={(e) => {
        e.stopPropagation()
        onSettings()
      }}
    >
      <Icon name="settings" size={grid ? 16 : 24} className={grid ? 'gear-ghost-icon' : 'card-icon-glyph'} />
    </button>
  )

  // Only rendered where the swap put it — list and the spacious grid. The standard grid keeps
  // opening through the whole tile (`wholeTileOpens`), so it never needs this element.
  const openAriaLabel = openState.fallbackOffer !== undefined ? openOverBluetoothLabel : openLabel
  // Only the large grid needs this — its plate lives on `.card`'s own `::before` and cannot hear
  // this button's native `:active` (see `controlPressed` above). List's `.card-gear` carries its
  // fill itself and answers to `:active` unassisted, so this is a no-op there.
  const openButtonPressHandlers = grid
    ? {
        onPointerDown: () => setControlPressed(true),
        onPointerUp: () => setControlPressed(false),
        onPointerCancel: () => setControlPressed(false),
        onPointerLeave: () => setControlPressed(false),
      }
    : {}
  const openButton = !swapped ? null : (
    <button
      className={grid ? 'card-corner-button' : 'card-gear'}
      type="button"
      aria-label={openAriaLabel}
      {...(sorting ? { tabIndex: -1 } : {})}
      /* **R7/D276 — an Open plate is disabled only while a CLOSE is in flight**, never while an
         open is; a gate with no Close can never reach this state, so its plate is byte-identical to
         today's (D280's *exactly one respect*). The visible report is the tile's own blue (D228);
         no `:disabled` treatment was minted, because a second appearance for a 1.6-second window is
         a new state on the app's most guarded control.

         **D295 keeps that half and reverses the other.** D276's reason for leaving an open
         unguarded was *the lease would refuse the second command anyway and the UI says so first* —
         which rests on D223's own asserting-rather-than-observing premise, and production
         disconfirmed it. The second tap is now dropped at dispatch in `runCommand` rather than
         disabled here: the standard grid's actuator is the whole tile and could never have carried
         the attribute, so a control-level guard would have covered two of the three Open paths. */
      {...(closingInFlight ? { disabled: true } : {})}
      {...openButtonPressHandlers}
      onClick={(e) => {
        e.stopPropagation()
        if (!sorting) onOpen()
      }}
    >
      {/* **D280 — one action, one glyph: the large grid's Open plate carries `key-round` on EVERY
          gate**, auto-close ones included, and the gate-type/personalized glyph is off it. Identity
          survives three times over on that tile (the plate's own fill, the accent bar at the foot,
          the name), and the actuator was the one place it competed with an affordance. `grid` is
          true here only in the spacious layout (`swapped` is false for the standard grid, which
          renders no `openButton` at all), so the list puck below keeps `iconName` and the standard
          grid's decorative plate is not reached from here — both deliberate (R7 §6). */}
      <Icon
        name={grid ? 'key-round' : iconName}
        size={grid ? 24 : 22}
        className={grid ? 'gear-ghost-icon' : ''}
      />
    </button>
  )

  /**
   * **R7/D276 — the Close plate, on the large grid and on a close-on-tap gate only.**
   *
   * Not on the list (its puck *is* the bite's geometry, D180 — a second puck there is a second
   * bite) and not on the standard grid (whose whole tile is the actuator, D197 (a)), which is R7
   * §6's own *a second control on either is a new tile, not a new button*.
   *
   * **One element carrying its own fill**, the list puck's shape rather than the Open plate's
   * two-element pair: `.card` has one `::before` and it is spent, so there is no plate to bridge a
   * press to and no `controlPressed` state to thread. Outlined rather than filled (§2.2) — the
   * pair reads as one control with a primary half.
   */
  const closeButton = !hasClose ? null : (
    <button
      className="card-corner-close"
      type="button"
      aria-label={closeLabel}
      {...(sorting ? { tabIndex: -1 } : {})}
      // Anything in flight on this tile disables it — a close behind an open is the same refused
      // second command as the reverse, and one lease answers both.
      {...(openState.phase === 'beaming' ? { disabled: true } : {})}
      onClick={(e) => {
        e.stopPropagation()
        if (!sorting) onClose()
      }}
    >
      <Icon name="lock" size={24} className="gear-ghost-icon" />
    </button>
  )

  /**
   * **A standing local fallback takes over the tile rather than adding a control to it.**
   *
   * The name line becomes *"tap again to open over Bluetooth"* and the sub-line says which
   * attempt failed, for the offer's whole 10-second life. Two things this is not: it is not a
   * second dispatch decided by the client (the resident's own second tap authorizes it, which is
   * PEC-13's rule and the reason D189(b) asked at all), and it is not a claim about the gate —
   * the generic *"couldn't open"* and PEC-8's *"sent, unconfirmed"* lines are what the sub-line
   * replaces, so neither is also printed above it (D189(f)).
   */
  const offering = openState.fallbackOffer !== undefined

  /**
   * **How many lines the meta below actually renders — counted here, in the component that
   * decides them, and never inferred from the DOM.**
   *
   * D181's large tile has no room for three: its 2/1 shape gives the column 128.7px at 375px,
   * and B's plate (54.6px) plus a three-line meta (74px) plus the 3px bar does not fit, so the
   * gate NAME renders behind the plate. D192 spends the tile's foot gap when that happens, and
   * this flag is what tells the stylesheet it has.
   *
   * **It was a `:has()` rule and could not stay one.** The sub-line is the last-used stamp,
   * which arrives from its own fetch *after* the first paint - so the tile renders two lines
   * with an empty `.gsub`, then becomes three. Chrome re-evaluated
   * `:has(> .gsub:not(:empty))` on that change; **iOS Safari did not**, and the tile that had
   * been correct a moment earlier stayed untightened for the rest of its life. It looked
   * intermittent - right after a view rotation (which rebuilds the tiles with the stamp already
   * in hand), wrong again after Gate Detail and back (a fresh mount, empty `.gsub` first) -
   * which is the shape of a selector-invalidation bug rather than a layout one. Counting here
   * cannot drift the same way: the count and the lines come from one render of one set of props.
   *
   * **D189(f) changed what an offer costs, and the terms move with it.** The offer no longer
   * renders a warning and a button — it renders the prompt on the name line and one sub-line —
   * but the prompt is a sentence where a gate name is a noun, so it wraps: `offering` is counted
   * as the second line the name takes, and the two lines it suppresses stop being counted at all.
   * Anything that reaches three is crowded, so an approximation in the safe direction costs
   * nothing.
   */
  const blockReason = openState.reason ?? (access.latched ? 'gate-latched' : access.reason)
  const hasSubLine =
    offering ||
    openState.phase === 'opened' ||
    openState.phase === 'beaming' ||
    (openState.phase === 'idle' && lastUsedText !== undefined)
  const metaLines =
    1 +
    (offering ? 1 : 0) +
    (blockReason !== null ? 1 : 0) +
    (openState.genericFailure === true && !offering ? 1 : 0) +
    // D192: every line that can render is counted here, or the tile's name loses room to it.
    (openState.notAuthorized === true ? 1 : 0) +
    (openState.unconfirmed === true && !offering ? 1 : 0) +
    // D192: the close's sentence takes a line exactly as the open's does, so it is counted here.
    (openState.closeFailed === true ? 1 : 0) +
    (bleLineText !== undefined ? 1 : 0) +
    (hasSubLine ? 1 : 0)

  const meta = (
    <div className={metaLines >= 3 ? 'meta crowded' : 'meta'}>
      <div className="gname">
        <span className={`gname-text${offering ? ' is-fallback-prompt' : ''}`}>
          {offering ? fallbackPromptText : name}
        </span>
        {manager ? (
          <Chip variant="manager">
            <Icon name="user" size={12} />
          </Chip>
        ) : null}
      </div>
      <AccessBlockLabel reason={blockReason} />
      {openState.genericFailure && !offering ? (
        <div className="shield-line closed">
          <span>{failedText}</span>
        </div>
      ) : null}
      {/*
       * R1-7(d): the named access refusal, never the generic message. **The two `409` lines that
       * stood beside it are gone** — R4/D223 routes `gate-busy` and `open-in-flight` to the
       * `opened` phase above, so neither reaches a sentence at all.
       */}
      {openState.notAuthorized ? (
        <div className="shield-line closed">
          <span>{notAuthorizedText}</span>
        </div>
      ) : null}
      {/*
       * PEC-8: a transport that could not confirm is not a gate that did not open — **and it is
       * printed only where nothing else says so.** With an offer standing, this tile's own name
       * line is already the second attempt's invitation and the sub-line names the failed one; a
       * device with no local path at all keeps the sentence, since there it is the whole report.
       */}
      {openState.unconfirmed && !offering ? (
        <div className="shield-line closed">
          <span>{unconfirmedText}</span>
        </div>
      ) : null}
      {/*
       * **R7/D277 (b) — the Failed close's own sentence.** It is a separate line rather than a
       * second value on the one above because the two vocabularies disagree: `gate-busy` is
       * Success for an open and a named Failure here, and `rejected` is the reverse. There is no
       * `!offering` guard because a close never leaves an offer standing — it has no BLE leaf to
       * fall back to (D277 (g)).
       */}
      {openState.closeFailed ? (
        <div className="shield-line closed">
          <span>{closeFailedText}</span>
        </div>
      ) : null}
      {/*
       * **D189(e) — what replaced the silent offline no-op.** Offline with no local path, the tap
       * used to do nothing at all; now it names which of the three reasons it is, in the words
       * PEC-25/D84 fix. Beside it, the local-failure copy, each value its own sentence.
       */}
      {bleLineText !== undefined ? (
        <div className="shield-line closed">
          <span>{bleLineText}</span>
        </div>
      ) : null}
      {/*
       * The connectivity LED is **removed from the tile** (D141). Home is an actuator: the tile
       * is the open command, and a dot reporting an unverified transport fact beside it answered
       * a question nobody asked here — Gate Detail keeps the LED, where the gate itself is the
       * subject. **P9's last-used stamp takes its line instead, for every role** (D104/D141):
       * the nearest alternative field on the record, `lastSeenAt`, means *the last time this
       * gate answered us*, which a status probe moves just as an open does, so rendering it as
       * "last opened" would report an opening the platform has no evidence for — the same claim
       * D134/D135 exist to forbid.
       */}
      <div className="gsub">
        {offering ? (
          /*
           * The small line, while the invitation is standing above it: **which attempt failed**,
           * and nothing about what the gate did. The last-used stamp below is idle-only, so this
           * takes a line nothing else wants.
           */
          <span>{cloudFailedText}</span>
        ) : openState.phase === 'opened' ? (
          /*
           * §6: **the result chip records the transport that actually carried the command**, not
           * the one the plan named — and after D189 those differ exactly when a fallback ran.
           * This is the only place the tile reports a transport: D141 removed the LED from Home
           * for a stated reason, and `hasCredential` is not reactive, so a *pre*-tap glyph here
           * would flicker on unrelated re-renders while re-litigating that decision.
           */
          <Chip variant="result">
            {/*
             * **D299 — a `409 gate-busy` says *sent* again, and what changed is its INPUT rather
             * than this chip.** D297 gave it *"already opening"* because the code covered both
             * directions, so *sent* was a claim about a command that might have been travelling the
             * other way. **D294 narrowed the code to an open holder** — a close answers
             * `gate-closing`, which is Failed and never reaches this chip — so D223's Success is
             * observed in direction as well as in existence, and the plain beat is true of it.
             */}
            {openState.usedTransport === 'ble'
              ? sentLocallyText
              : openState.pedestrian
                ? pedestrianOpenedText
                : openedText}
          </Chip>
        ) : openState.phase === 'beaming' ? (
          <span>{openingText}</span>
        ) : /*
         * D104/D121/D141/D163 — the viewer's own last-used stamp, every role. Idle-only, so it
         * never collides with the transient "Sending…"/"Sent" text above.
         */
        openState.phase === 'idle' && lastUsedText !== undefined ? (
          <span>{lastUsedText}</span>
        ) : null}
      </div>
    </div>
  )

  // Only the standard (non-spacious) grid still opens on a tap anywhere on the tile — everywhere
  // else, opening and settings are each their own icon, so the rest of the tile (name, meta,
  // background) is inert. `cardIsControl` gates the card's own role/tabIndex/aria-label: sorting
  // always needs them (keyboard reorder), `wholeTileOpens` needs them the rest of the time, and
  // list/spacious with nothing standing needs neither — a focusable region with no action of its
  // own reads worse to a screen reader than no region at all.
  const cardIsControl = sorting || wholeTileOpens

  return (
    <div
      ref={cardRef}
      className={`card${latchedClass}${greyedClass}${subLowClass}${commandClass}${offering ? ' offering' : ''}${sorting ? ' reorder-mode' : ''}${controlPressedClass}`}
      style={{ ['--accent' as string]: accent.fg, ['--wash' as string]: accent.bg }}
      {...(cardIsControl ? { role: 'button' as const, tabIndex: 0, 'aria-label': label } : {})}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onHoldPointerLeave}
      // D113's long-press already owns this gesture; Android's own long-press handling (a text
      // selection callout, or — on some WebViews — a bare context menu) is a second answer to the
      // same touch and must not appear on top of it.
      onContextMenu={(event) => event.preventDefault()}
      onClick={() => {
        if (!sorting && wholeTileOpens) onOpen()
      }}
      onKeyDown={(e) => {
        if (sorting) {
          if (e.key === ' ') {
            e.preventDefault()
            onPickUp()
          }
          if (pickedUp && (e.key === 'ArrowUp' || e.key === (dir === 'rtl' ? 'ArrowLeft' : 'ArrowUp'))) {
            e.preventDefault()
            onMoveUp()
          }
          if (pickedUp && (e.key === 'ArrowDown' || e.key === (dir === 'rtl' ? 'ArrowRight' : 'ArrowDown'))) {
            e.preventDefault()
            onMoveDown()
          }
          return
        }
        if (wholeTileOpens && e.key === 'Enter') onOpen()
      }}
    >
      {grid ? (
        swapped ? (
          <>
            {settingsButton}
            {openButton}
            {closeButton}
            {meta}
          </>
        ) : (
          <>
            <Icon name={iconName} size={24} className="card-glyph" />
            {settingsButton}
            {meta}
          </>
        )
      ) : (
        <>
          <div className="card-main">
            {settingsButton}
            {meta}
          </div>
          {openButton}
        </>
      )}
    </div>
  )
}
